import { useAgentDelegation, useWalletDisconnectDetection, WalletDisconnectEvent, waitForBinanceWallet, getBinanceProvider, isMetaMaskProviderAvailable, isBitgetProviderAvailable, getBitgetProvider, waitForBitgetWallet, isTokenPocketProviderAvailable, getTokenPocketProvider, waitForTokenPocketWallet, isTokenPocketEthereumAvailable as isTokenPocketEthereumAvailableHook, isTokenPocketSolanaAvailable as isTokenPocketSolanaAvailableHook, isCoinbaseWalletProviderAvailableSync, getCoinbaseWalletProvider, waitForCoinbaseWallet, isCoinbaseWalletSolanaAvailable as isCoinbaseWalletSolanaAvailableHook } from "../hooks";
import { useOnboard } from "../hooks/useOnboard";
import {
  useConnect,
  useDisconnect,
  useAccount,
  useSignTypedData,
  useChainId,
} from "wagmi";
import { useEffect, useState, useRef } from "react";
import { 
  connectPhantomSolana, 
  getPhantomSolanaProvider,
  isPhantomSolanaAvailable,
  connectOneKeySolana,
  getOneKeySolanaProvider,
  signOneKeySolanaMessage,
  isBinanceSolanaAvailable,
  getBinanceSolanaProvider,
  connectBinanceSolana,
  signBinanceSolanaMessage,
  isBitgetSolanaAvailable,
  getBitgetSolanaProvider,
  connectBitgetSolana,
  signBitgetSolanaMessage,
  isTokenPocketSolanaAvailable,
  getTokenPocketSolanaProvider,
  connectTokenPocketSolana,
  signTokenPocketSolanaMessage,
  isCoinbaseWalletSolanaAvailable,
  getCoinbaseWalletSolanaProvider,
  connectCoinbaseWalletSolana,
  signCoinbaseWalletSolanaMessage,
} from "../sdk/signing/solana";
import {
  getBitcoinAddress,
  getPreferredBitcoinWallet,
  isBitcoinWalletAvailable,
  isOKXWalletInstalled
} from "../sdk/signing/bitcoin";
import {
  isOKXWalletAvailable as isOKXWalletSDKAvailable,
  isOKXEthereumAvailable as isOKXEthereumSDKAvailable,
  isOKXSolanaAvailable as isOKXSolanaSDKAvailable,
  isOKXBitcoinAvailable as isOKXBitcoinSDKAvailable,
  signOKXEthereumTypedData,
  signOKXSolanaMessage,
  signOKXBitcoinMessage,
  getOKXEthereumAddress,
  getOKXSolanaAddress,
  getOKXBitcoinAddress,
  getOKXSolanaProvider,
} from "../sdk/signing/okx";
import {
  isOneKeySolanaAvailable as isOneKeySolanaSDKAvailable,
  isOneKeyBitcoinAvailable as isOneKeyBitcoinSDKAvailable,
  isOneKeyEthereumAvailable as isOneKeyEthereumSDKAvailable,
  signOneKeyBitcoinMessage,
  signOneKeyEthereumTypedData,
  getOneKeyBitcoinAddress,
} from "../sdk/signing/onekey";
import { mormSigner, AgentDelegationOptions } from "../sdk";
import { getBestWallet as getBestWalletName } from "../sdk/utils/extdetection";

function AgentDelegation(): JSX.Element {
  // Integrate with useOnboard for better UX (uses persisted mnemonic/address if available)
  const onboard = useOnboard();
  
  const {
    ownerAddress,
    setOwnerAddress,
    agentAddress,
    setAgentAddress,
    agentPrivateKey,
    expiresInDays,
    setExpiresInDays,
    permissions,
    setPermissions,
    agentName,
    setAgentName,
    nonce,
    setNonce,
    result,
    loading,
    generateAgentKeypair,
    clearAgentCredentials,
    isMetaMaskConnected,
    isPhantomConnected,
    isOneKeyConnected,
    isOKXConnected,
    isBinanceConnected,
    isBitgetConnected,
    isRainbowConnected,
    isReady,
  } = useAgentDelegation(onboard); // Pass onboard hook for integration

  const { connect, connectors, isPending: isConnecting } = useConnect();
  const { disconnect } = useDisconnect();
  
  // Wagmi v3: useAccount is still available and works well
  // useConnection provides connection-specific info but useAccount is simpler for our use case
  const { address: wagmiAddress, isConnected: wagmiIsConnected } = useAccount();
  const { signTypedDataAsync } = useSignTypedData();
  const chainId = useChainId();

  // State to store available addresses from wallet connection
  const [availableAddresses, setAvailableAddresses] = useState<string[]>([]);
  // Chain type for wallet connection (ethereum, solana, or bitcoin)
  const [connectionChainType, setConnectionChainType] = useState<"ethereum" | "solana" | "bitcoin">("ethereum");
  // Local state for signing result (to avoid WASM dependency)
  const [signingResult, setSigningResult] = useState<{
    success: boolean;
    signature?: string;
    typedData?: any;
    error?: string;
  } | null>(null);
  const [isSigning, setIsSigning] = useState(false);
  // Track if credentials were restored from sessionStorage
  const [credentialsRestored, setCredentialsRestored] = useState(false);

  // Helper function to prepare EIP-712 typed data
  // Tries to use mormSigner.prepareAgentSign() for consistency, falls back to manual preparation
  const prepareTypedDataForSigning = async (): Promise<{
    domain: any;
    types: any;
    primaryType: "AgentApproval";
    message: any;
  }> => {
    const currentOwnerAddress = wagmiAddress || ownerAddress;
    if (!currentOwnerAddress || !agentAddress) {
      throw new Error("Owner and agent addresses are required");
    }

    const permissionsArray = permissions
      .split(",")
      .map((p) => p.trim())
      .filter((p) => p.length > 0);
    const deadline =
      Math.floor(Date.now() / 1000) + expiresInDays * 24 * 60 * 60;

    // Try to use mormSigner for consistency with WASM implementation
    if (mormSigner.isReady()) {
      try {
        const options: AgentDelegationOptions = {
          owner: currentOwnerAddress.toLowerCase(),
          agent: agentAddress.toLowerCase(),
          expiresInDays: expiresInDays,
          permissions: permissionsArray,
          nonce: nonce || Date.now().toString(),
          agentName: agentName || "",
        };

        const prepareResult = await mormSigner.prepareAgentSign(options);
        if (prepareResult.success && prepareResult.typedData) {
          // mormSigner returns typedData in the format expected by wallets
          // Ensure it has the structure we need
          if (prepareResult.typedData.domain && prepareResult.typedData.types && prepareResult.typedData.message) {
            return {
              domain: prepareResult.typedData.domain,
              types: prepareResult.typedData.types,
              primaryType: "AgentApproval" as const,
              message: prepareResult.typedData.message,
            };
          }
        }
      } catch (error) {
        console.warn("Failed to use mormSigner.prepareAgentSign, falling back to manual preparation:", error);
        // Fall through to manual preparation
      }
    }

    // Fallback: Manual preparation (matches mormSigner structure)
    const domain = {
      name: "Morpheum",
      version: "",
      chainId: chainId,
      verifyingContract:
        "0x0000000000000000000000000000000000000000" as `0x${string}`, // Placeholder
    };

    const types = {
      EIP712Domain: [
        { name: "name", type: "string" },
        { name: "version", type: "string" },
        { name: "chainId", type: "uint256" },
        { name: "verifyingContract", type: "address" },
      ],
      AgentApproval: [
        { name: "owner", type: "address" },
        { name: "agent", type: "address" },
        { name: "agentName", type: "string" },
        { name: "expiresInDays", type: "uint256" },
        { name: "permissions", type: "string[]" },
        { name: "nonce", type: "string" },
        { name: "deadline", type: "uint256" },
      ],
    };

    const message = {
      owner: currentOwnerAddress.toLowerCase(),
      agent: agentAddress.toLowerCase(),
      agentName: agentName || "",
      expiresInDays: expiresInDays,
      permissions: permissionsArray,
      nonce: nonce || Date.now().toString(),
      deadline: deadline,
    };

    return {
      domain,
      types,
      primaryType: "AgentApproval" as const,
      message,
    };
  };

  // Direct signing function that doesn't use WASM
  const handleSignAgentApproval = async () => {
    const currentOwnerAddress = wagmiAddress || ownerAddress;
    if (!currentOwnerAddress || !agentAddress) {
      setSigningResult({
        success: false,
        error: "Owner and agent addresses are required",
      });
      return;
    }

    setIsSigning(true);
    setSigningResult(null);

    try {
      // Check if OneKey wallet is connected and use OneKey SDK for signing
      // For Ethereum: check if OneKey is connected via wagmi or direct detection
      // For Solana/Bitcoin: check connection state directly
      const isOneKeyWalletConnected = 
        (connectionChainType === "ethereum" && isOneKeyConnected && isOneKeyEthereumAvailableSDK) ||
        (connectionChainType === "solana" && isSolanaConnected && isOneKeySolanaAvailable && getOneKeySolanaProvider()?.isConnected) ||
        (connectionChainType === "bitcoin" && isBitcoinConnected && isOneKeyBitcoinAvailable);
      const useOneKeySDK = isOneKeyWalletConnected;

      // Check if OKX wallet is connected and use OKX SDK for signing
      // For Ethereum: use isOKXConnected (wagmi-based)
      // For Solana/Bitcoin: check connection state directly
      const isOKXWalletConnected = 
        (connectionChainType === "ethereum" && isOKXConnected && isOKXWalletSDKAvailable()) ||
        (connectionChainType === "solana" && isSolanaConnected && isOKXSolanaSDKAvailable() && getOKXSolanaProvider()?.isConnected) ||
        (connectionChainType === "bitcoin" && isBitcoinConnected && isOKXBitcoinSDKAvailable());
      const useOKXSDK = isOKXWalletConnected;

      if (useOKXSDK && connectionChainType === "ethereum" && isOKXEthereumSDKAvailable()) {
        // Use OKX SDK for Ethereum EIP-712 signing
        const typedData = await prepareTypedDataForSigning();
        const okxResult = await signOKXEthereumTypedData(
          typedData.domain,
          typedData.types,
          typedData.message,
          currentOwnerAddress
        );

        setSigningResult({
          success: true,
          signature: okxResult.signature,
          typedData: typedData,
        });
      } else if (useOneKeySDK && connectionChainType === "ethereum" && isOneKeyEthereumAvailableSDK) {
        // Use OneKey SDK for Ethereum EIP-712 signing
        const typedData = await prepareTypedDataForSigning();
        const oneKeyResult = await signOneKeyEthereumTypedData(
          typedData.domain,
          typedData.types,
          typedData.message,
          currentOwnerAddress
        );

        setSigningResult({
          success: true,
          signature: oneKeyResult.signature,
          typedData: typedData,
        });
      } else if (useOneKeySDK && connectionChainType === "solana" && isOneKeySolanaAvailable) {
        // Use OneKey SDK for Solana message signing
        const typedData = await prepareTypedDataForSigning();
        const oneKeySolanaProvider = getOneKeySolanaProvider();
        if (!oneKeySolanaProvider) {
          throw new Error("OneKey Solana provider not found");
        }
        // Construct Solana message payload from typed data
        const permissionsArray = permissions
          .split(",")
          .map((p) => p.trim())
          .filter((p) => p.length > 0);
        const deadline = Math.floor(Date.now() / 1000) + expiresInDays * 24 * 60 * 60;
        const payload = {
          action: "AgentApproval",
          owner: typedData.message.owner || currentOwnerAddress,
          agent: typedData.message.agent || agentAddress,
          expiresInDays: typedData.message.expiresInDays || expiresInDays,
          permissions: typedData.message.permissions || permissionsArray,
          nonce: typedData.message.nonce || nonce || Date.now().toString(),
          agentName: typedData.message.agentName || agentName || "",
          deadline: typedData.message.deadline?.toString() || deadline.toString(),
        };
        const oneKeyResult = await signOneKeySolanaMessage(oneKeySolanaProvider, payload);

        setSigningResult({
          success: true,
          signature: oneKeyResult.signature,
          typedData: typedData,
        });
      } else if (useOKXSDK && connectionChainType === "solana" && isOKXSolanaSDKAvailable()) {
        // Use OKX SDK for Solana message signing
        const typedData = await prepareTypedDataForSigning();
        const message = JSON.stringify(typedData.message);
        const okxResult = await signOKXSolanaMessage(message);

        setSigningResult({
          success: true,
          signature: okxResult.signature,
          typedData: typedData,
        });
      } else if (connectionChainType === "solana" && isSolanaConnected && isBitgetSolanaAvailable() && getBitgetSolanaProvider()?.isConnected) {
        // Use Bitget SDK for Solana message signing
        const typedData = await prepareTypedDataForSigning();
        const bitgetSolanaProvider = getBitgetSolanaProvider();
        if (!bitgetSolanaProvider) {
          throw new Error("Bitget Solana provider not found");
        }
        // Construct Solana message payload from typed data
        const permissionsArray = permissions
          .split(",")
          .map((p) => p.trim())
          .filter((p) => p.length > 0);
        const deadline = Math.floor(Date.now() / 1000) + expiresInDays * 24 * 60 * 60;
        const payload = {
          action: "AgentApproval",
          owner: typedData.message.owner || currentOwnerAddress,
          agent: typedData.message.agent || agentAddress,
          expiresInDays: typedData.message.expiresInDays || expiresInDays,
          permissions: typedData.message.permissions || permissionsArray,
          nonce: typedData.message.nonce || nonce || Date.now().toString(),
          agentName: typedData.message.agentName || agentName || "",
          deadline: typedData.message.deadline?.toString() || deadline.toString(),
        };
        const bitgetResult = await signBitgetSolanaMessage(bitgetSolanaProvider, payload);
        setSigningResult({
          success: true,
          signature: bitgetResult.signature,
          typedData: typedData,
        });
      } else if (connectionChainType === "solana" && isSolanaConnected && isTokenPocketSolanaAvailable() && getTokenPocketSolanaProvider()?.isConnected) {
        // Use TokenPocket SDK for Solana message signing
        const typedData = await prepareTypedDataForSigning();
        const tokenPocketSolanaProvider = getTokenPocketSolanaProvider();
        if (!tokenPocketSolanaProvider) {
          throw new Error("TokenPocket Solana provider not found");
        }
        // Construct Solana message payload from typed data
        const permissionsArray = permissions
          .split(",")
          .map((p) => p.trim())
          .filter((p) => p.length > 0);
        const deadline = Math.floor(Date.now() / 1000) + expiresInDays * 24 * 60 * 60;
        const payload = {
          action: "AgentApproval",
          owner: typedData.message.owner || currentOwnerAddress,
          agent: typedData.message.agent || agentAddress,
          expiresInDays: typedData.message.expiresInDays || expiresInDays,
          permissions: typedData.message.permissions || permissionsArray,
          nonce: typedData.message.nonce || nonce || Date.now().toString(),
          agentName: typedData.message.agentName || agentName || "",
          deadline: typedData.message.deadline?.toString() || deadline.toString(),
        };
        const tokenPocketResult = await signTokenPocketSolanaMessage(tokenPocketSolanaProvider, payload);
        setSigningResult({
          success: true,
          signature: tokenPocketResult.signature,
          typedData: typedData,
        });
      } else if (connectionChainType === "solana" && isSolanaConnected && isBinanceSolanaAvailable() && getBinanceSolanaProvider()?.isConnected) {
        // Use Binance SDK for Solana message signing
        const typedData = await prepareTypedDataForSigning();
        const binanceSolanaProvider = getBinanceSolanaProvider();
        if (!binanceSolanaProvider) {
          throw new Error("Binance Solana provider not found");
        }
        // Construct Solana message payload from typed data
        const permissionsArray = permissions
          .split(",")
          .map((p) => p.trim())
          .filter((p) => p.length > 0);
        const deadline = Math.floor(Date.now() / 1000) + expiresInDays * 24 * 60 * 60;
        const payload = {
          action: "AgentApproval",
          owner: typedData.message.owner || currentOwnerAddress,
          agent: typedData.message.agent || agentAddress,
          expiresInDays: typedData.message.expiresInDays || expiresInDays,
          permissions: typedData.message.permissions || permissionsArray,
          nonce: typedData.message.nonce || nonce || Date.now().toString(),
          agentName: typedData.message.agentName || agentName || "",
          deadline: typedData.message.deadline?.toString() || deadline.toString(),
        };
        const binanceResult = await signBinanceSolanaMessage(binanceSolanaProvider, payload);

        setSigningResult({
          success: true,
          signature: binanceResult.signature,
          typedData: typedData,
        });
      } else if (useOneKeySDK && connectionChainType === "bitcoin" && isOneKeyBitcoinAvailable) {
        // Use OneKey SDK for Bitcoin message signing
        const typedData = await prepareTypedDataForSigning();
        const message = JSON.stringify(typedData.message);
        if (!currentOwnerAddress) {
          throw new Error("Owner address is required for Bitcoin signing");
        }
        const oneKeyResult = await signOneKeyBitcoinMessage(message, currentOwnerAddress, 'ecdsa');

        setSigningResult({
          success: true,
          signature: oneKeyResult.signature,
          typedData: typedData,
        });
      } else if (connectionChainType === "bitcoin" && isBitcoinConnected && isOKXBitcoinSDKAvailable()) {
        // Use OKX SDK for Bitcoin message signing
        const typedData = await prepareTypedDataForSigning();
        const message = JSON.stringify(typedData.message);
        if (!currentOwnerAddress) {
          throw new Error("Owner address is required for Bitcoin signing");
        }
        const okxResult = await signOKXBitcoinMessage(message, currentOwnerAddress);

        setSigningResult({
          success: true,
          signature: okxResult.signature,
          typedData: typedData,
        });
      } else if (connectionChainType === "ethereum" && isTokenPocketEthereumDetected && wagmiIsConnected) {
        // Use TokenPocket Ethereum wallet signing via wagmi
        const typedData = await prepareTypedDataForSigning();
        const signature = await signTypedDataAsync({
          domain: typedData.domain as any,
          types: typedData.types as any,
          primaryType: typedData.primaryType,
          message: typedData.message as any,
        });

        setSigningResult({
          success: true,
          signature: signature,
          typedData: typedData,
        });
      } else if (connectionChainType === "ethereum" && isCoinbaseWalletEthereumDetected && wagmiIsConnected) {
        // Use Coinbase Wallet Ethereum signing via wagmi
        const typedData = await prepareTypedDataForSigning();
        const signature = await signTypedDataAsync({
          domain: typedData.domain as any,
          types: typedData.types as any,
          primaryType: typedData.primaryType,
          message: typedData.message as any,
        });

        setSigningResult({
          success: true,
          signature: signature,
          typedData: typedData,
        });
      } else if (connectionChainType === "solana" && isSolanaConnected && isCoinbaseWalletSolanaDetected && getCoinbaseWalletSolanaProvider()?.isConnected) {
        // Use Coinbase Wallet SDK for Solana message signing
        const typedData = await prepareTypedDataForSigning();
        const coinbaseSolanaProvider = getCoinbaseWalletSolanaProvider();
        if (!coinbaseSolanaProvider) {
          throw new Error("Coinbase Wallet Solana provider not found");
        }
        // Construct Solana message payload from typed data
        const permissionsArray = permissions
          .split(",")
          .map((p) => p.trim())
          .filter((p) => p.length > 0);
        const deadline = Math.floor(Date.now() / 1000) + expiresInDays * 24 * 60 * 60;
        const payload = {
          action: "AgentApproval",
          owner: typedData.message.owner || currentOwnerAddress,
          agent: typedData.message.agent || agentAddress,
          expiresInDays: typedData.message.expiresInDays || expiresInDays,
          permissions: typedData.message.permissions || permissionsArray,
          nonce: typedData.message.nonce || nonce || Date.now().toString(),
          agentName: typedData.message.agentName || agentName || "",
          deadline: typedData.message.deadline?.toString() || deadline.toString(),
        };
        const coinbaseResult = await signCoinbaseWalletSolanaMessage(coinbaseSolanaProvider, payload);

        setSigningResult({
          success: true,
          signature: coinbaseResult.signature,
          typedData: typedData,
        });
      } else {
        // Use standard wagmi signing for Ethereum (works with MetaMask, Phantom Ethereum, OneKey, OKX via wagmi)
        // Prepare typed data directly (no WASM)
        // prepareTypedDataForSigning will use wagmiAddress if available, otherwise ownerAddress
        const typedData = await prepareTypedDataForSigning();

        // Sign with wallet (this will pop the wallet dialog)
        // wagmi expects the typed data in a specific format
        const signature = await signTypedDataAsync({
          domain: typedData.domain as any,
          types: typedData.types as any,
          primaryType: typedData.primaryType,
          message: typedData.message as any,
        });

        // Set result with signature and typed data
        setSigningResult({
          success: true,
          signature: signature,
          typedData: typedData,
        });
      }
    } catch (error: any) {
      if (
        error.code === 4001 ||
        error.message?.includes("User rejected") ||
        error.message?.includes("rejected")
      ) {
        setSigningResult({
          success: false,
          error: "User rejected the signature request",
        });
      } else {
        console.error("Error signing:", error);
        setSigningResult({
          success: false,
          error: error.message || "Unknown error during signing",
        });
      }
    } finally {
      setIsSigning(false);
    }
  };

  // Check if wallets are available (using comprehensive MetaMask detection)
  const isMetaMaskAvailable = isMetaMaskProviderAvailable();
  
  // Check if Bitget wallet is available
  const isBitgetAvailable = isBitgetProviderAvailable();

  // Check if TokenPocket wallet is available (multi-chain wallet)
  // TokenPocket supports Ethereum and Solana chains
  const isTokenPocketAvailable = isTokenPocketProviderAvailable();
  const isTokenPocketEthereumDetected = isTokenPocketEthereumAvailableHook();
  const isTokenPocketSolanaDetected = isTokenPocketSolanaAvailableHook();
  const isCoinbaseWalletEthereumDetected = isCoinbaseWalletProviderAvailableSync();
  const isCoinbaseWalletSolanaDetected = isCoinbaseWalletSolanaAvailableHook();

  // Rainbow Wallet detection
  const isRainbowAvailable =
    typeof window !== "undefined" &&
    (window as any).ethereum?.isRainbow === true;

  // Phantom detection: exclude OneKey masquerading as Phantom
  // OneKey provides window.phantom for backward compatibility, so we need to check for OneKey first
  const isPhantomAvailable =
    typeof window !== "undefined" &&
    (!!(window as any).phantom?.ethereum ||
      !!(window as any).ethereum?.isPhantom) &&
    // Exclude if it's actually OneKey (OneKey provides window.phantom for backward compatibility)
    !((window as any).$onekey || (window as any).ethereum?.isOneKey);

  const isOneKeyAvailable =
    typeof window !== "undefined" &&
    (!!(window as any).$onekey?.ethereum ||
      !!(window as any).ethereum?.isOneKey ||
      !!(window as any).$onekey); // Check for main OneKey wallet object

  // OneKey multi-chain detection
  const isOneKeyEthereumAvailableSDK = isOneKeyEthereumSDKAvailable();
  const isOneKeySolanaAvailable = isOneKeySolanaSDKAvailable();
  const isOneKeyBitcoinAvailable = isOneKeyBitcoinSDKAvailable();

  const isOKXEthereumAvailable =
    typeof window !== "undefined" &&
    (!!(window as any).okxwallet?.ethereum ||
      !!(window as any).ethereum?.isOKX);

  // Binance wallet detection with inpage polling support
  // State to track Binance availability (updated via polling)
  // Modern detection: window.binancew3w.isExtension (primary)
  // Legacy detection: window.BinanceChain, window.ethereum with Binance properties
  // Also detects via Solana provider: window.solana, window.binancew3w.solana
  const [isBinanceAvailable, setIsBinanceAvailable] = useState(() => {
    if (typeof window === "undefined") return false;
    // Immediate check - modern Binance Web3 Wallet
    if ((window as any).binancew3w?.isExtension === true) {
      return true;
    }
    // Legacy checks
    if ((window as any).BinanceChain) {
      return true;
    }
    const ethereum = (window as any).ethereum;
    if (ethereum?.isBinance) {
      return true;
    }
    // Check provider name
    if (ethereum) {
      const providerName = ethereum.providerName || ethereum.name;
      if (typeof providerName === "string" && providerName.toLowerCase().includes("binance")) {
        return true;
      }
    }
    // Fallback: Binance-specific properties
    if (ethereum?._postMessageToMp &&
        typeof ethereum._postMessageToMp === "function" &&
        ethereum?.namespace === "eip155") {
      return true;
    }
    // Check Binance Solana provider
    if (isBinanceSolanaAvailable()) {
      return true;
    }
    return false;
  });

  // Poll for Binance wallet availability (handles async injection timing)
  useEffect(() => {
    if (isBinanceAvailable) return; // Already detected, no need to poll
    
    let mounted = true;
    
    // Wait for Binance wallet with polling (3 second timeout, 100ms interval)
    waitForBinanceWallet(3000, 100).then((detected) => {
      if (mounted && detected) {
        setIsBinanceAvailable(true);
      }
    });
    
    return () => {
      mounted = false;
    };
  }, [isBinanceAvailable]);

  // Find connectors for supported wallets
  // In Wagmi v3, connectors are available via useConnect hook
  // The injected connector should be available if any wallet is installed
  const injectedConnector = connectors.find((c) => 
    c.id === "injected" || 
    c.type === "injected" ||
    (c as any).name?.toLowerCase().includes("injected")
  );

  // Debug logging on component mount and when connectors change
  useEffect(() => {
    console.log("[Wallet Debug] Component mounted/updated");
    console.log(
      "[Wallet Debug] Window object:",
      typeof window !== "undefined" ? "available" : "undefined",
    );
    console.log("[Wallet Debug] window.phantom:", (window as any).phantom);
    console.log("[Wallet Debug] window.ethereum:", (window as any).ethereum);
    console.log(
      "[Wallet Debug] window.ethereum?.isMetaMask:",
      (window as any).ethereum?.isMetaMask,
    );
    console.log(
      "[Wallet Debug] window.ethereum?.isPhantom:",
      (window as any).ethereum?.isPhantom,
    );
    console.log(
      "[Wallet Debug] window.ethereum?.isOneKey:",
      (window as any).ethereum?.isOneKey,
    );
    console.log(
      "[Wallet Debug] window.ethereum?.isOKX:",
      (window as any).ethereum?.isOKX,
    );
    console.log(
      "[Wallet Debug] window.ethereum?.isBinance:",
      (window as any).ethereum?.isBinance,
    );
    console.log(
      "[Wallet Debug] window.binancew3w:",
      (window as any).binancew3w,
    );
    console.log(
      "[Wallet Debug] window.binancew3w?.isExtension:",
      (window as any).binancew3w?.isExtension,
    );
    console.log(
      "[Wallet Debug] window.ethereum?.chainId:",
      (window as any).ethereum?.chainId,
    );
    console.log(
      "[Wallet Debug] window.ethereum?.networkVersion:",
      (window as any).ethereum?.networkVersion,
    );
    // Check for Binance-specific properties
    const ethereum = (window as any).ethereum;
    if (ethereum) {
      console.log(
        "[Wallet Debug] window.ethereum.providerName:",
        ethereum.providerName || ethereum.name,
      );
      console.log(
        "[Wallet Debug] window.ethereum keys:",
        Object.keys(ethereum).filter((k) => 
          k.toLowerCase().includes('binance') || 
          k.toLowerCase().includes('bnb') ||
          k === '_postMessageToMp' ||
          k === '_requestNormalConnect' ||
          k === 'namespace' ||
          k === 'providerName' ||
          k === 'name'
        ),
      );
      console.log(
        "[Wallet Debug] window.ethereum.namespace:",
        ethereum.namespace,
      );
      console.log(
        "[Wallet Debug] window.ethereum._postMessageToMp:",
        typeof ethereum._postMessageToMp,
      );
    }
    console.log("[Wallet Debug] window.BinanceChain:", (window as any).BinanceChain);
    console.log("[Wallet Debug] window.BinanceChain?.solana:", (window as any).BinanceChain?.solana);
    console.log("[Wallet Debug] window.solana:", (window as any).solana);
    console.log("[Wallet Debug] window.solana?.isBinance:", (window as any).solana?.isBinance);
    console.log("[Wallet Debug] window.solana?.name:", (window as any).solana?.name);
    console.log("[Wallet Debug] window.binancew3w?.solana:", (window as any).binancew3w?.solana);
    console.log("[Wallet Debug] window.ethereum?.solana:", (window as any).ethereum?.solana);
    console.log("[Wallet Debug] isBinanceSolanaAvailable():", isBinanceSolanaAvailable());
    console.log("[Wallet Debug] window.$onekey:", (window as any).$onekey);
    console.log("[Wallet Debug] window.okxwallet:", (window as any).okxwallet);
    console.log("[Wallet Debug] window.okxwallet?.ethereum:", (window as any).okxwallet?.ethereum);
    console.log("[Wallet Debug] window.okxwallet?.bitcoin:", (window as any).okxwallet?.bitcoin);
    console.log("[Wallet Debug] window.unisat:", (window as any).unisat);
    console.log("[Wallet Debug] window.BitcoinProvider:", (window as any).BitcoinProvider);
    console.log("[Wallet Debug] window.XverseProvider:", (window as any).XverseProvider);
    console.log("[Wallet Debug] isPhantomAvailable:", isPhantomAvailable);
    console.log("[Wallet Debug] isOneKeyAvailable:", isOneKeyAvailable);
    console.log("[Wallet Debug] isOKXEthereumAvailable:", isOKXEthereumAvailable);
    console.log("[Wallet Debug] isBinanceAvailable:", isBinanceAvailable);
    console.log("[Wallet Debug] isBitcoinWalletAvailable():", isBitcoinWalletAvailable());
    console.log("[Wallet Debug] isOKXWalletInstalled():", isOKXWalletInstalled());
    console.log("[Wallet Debug] All connectors:", connectors);
    console.log("[Wallet Debug] Injected connector:", injectedConnector);
    console.log(
      "[Wallet Debug] Injected connector ID:",
      injectedConnector?.id,
    );
    console.log(
      "[Wallet Debug] Injected connector name:",
      injectedConnector?.name,
    );
    console.log("[Wallet Debug] isConnecting:", isConnecting);
  }, [connectors, injectedConnector, isPhantomAvailable, isOneKeyAvailable, isOKXEthereumAvailable, isBinanceAvailable, isConnecting]);

  // Determine which wallet is connected (moved before debug useEffect that uses these)
  // Use fallback: if wagmi has an address that matches ownerAddress, consider it connected
  // This handles cases where wagmi state hasn't updated yet but we have the address
  const hasMatchingAddress =
    ownerAddress &&
    wagmiAddress &&
    ownerAddress.toLowerCase() === wagmiAddress.toLowerCase();

  // Check wallet type from detection
  // IMPORTANT: Use the same logic as detectSupportedWallet() to ensure consistency
  // Check OKX before MetaMask to avoid false positives
  // Smart Heuristic: If OKX Solana AND Bitcoin are both detected, then Ethereum is very likely OKX
  // Priority: OneKey > OKX > Bitget > TokenPocket > Coinbase Wallet > Rainbow > Binance > Phantom > MetaMask
  // OneKey provides backward compatibility (window.phantom, window.unisat) so must be checked first
  let detectedWallet: "Phantom" | "OneKey" | "OKX" | "Bitget" | "TokenPocket" | "Coinbase Wallet" | "Rainbow" | "Binance" | "MetaMask" | null = null;
  
  if (typeof window !== "undefined") {
    // Check for OneKey FIRST (highest priority - provides backward compatibility interfaces)
    // OneKey provides window.phantom, window.unisat, etc. for backward compatibility
    // So if window.$onekey exists or window.ethereum.isOneKey is true, it's definitely OneKey
    if ((window as any).$onekey?.ethereum || (window as any).ethereum?.isOneKey || (window as any).$onekey) {
      detectedWallet = "OneKey";
    }
    // Check for Phantom (only if OneKey is not detected)
    // Note: OneKey provides window.phantom for backward compatibility, so this check comes after OneKey
    else if ((window as any).phantom?.ethereum || (window as any).ethereum?.isPhantom) {
      detectedWallet = "Phantom";
    }
    // Smart OKX Detection: If both Solana and Bitcoin are OKX, then Ethereum is OKX
    else {
      const hasOKXSolana = !!(window as any).okxwallet?.solana;
      const hasOKXBitcoin = !!(window as any).okxwallet?.bitcoin;
      const hasOKXMultiChain = hasOKXSolana && hasOKXBitcoin;
      
      // If both Solana and Bitcoin are OKX, then Ethereum is definitely OKX
      if (hasOKXMultiChain && (window as any).ethereum) {
        detectedWallet = "OKX";
      }
      // Standard OKX detection
      else if ((window as any).okxwallet?.ethereum ||
               (window as any).ethereum?.isOKX ||
               (window as any).okxwallet) {
        detectedWallet = "OKX";
      }
      // Check for Bitget (must be before TokenPocket, Coinbase, Binance, and MetaMask!)
      else if (isBitgetProviderAvailable()) {
        detectedWallet = "Bitget";
      }
      // Check for TokenPocket (must be before Coinbase, Binance, and MetaMask!)
      else if (isTokenPocketProviderAvailable()) {
        detectedWallet = "TokenPocket";
      }
      // Check for Coinbase Wallet (must be before Rainbow, Binance, and MetaMask!)
      else if (isCoinbaseWalletProviderAvailableSync()) {
        detectedWallet = "Coinbase Wallet";
      }
      // Check for Rainbow Wallet (must be before Binance and MetaMask!)
      else if ((window as any).ethereum?.isRainbow === true) {
        detectedWallet = "Rainbow";
      }
      // Check for Binance (must be before MetaMask)
      else if ((window as any).BinanceChain || (window as any).ethereum?.isBinance) {
        detectedWallet = "Binance";
      }
      // Check for MetaMask using comprehensive detection (only if OKX, Bitget, TokenPocket, Coinbase, and Binance are not detected)
      else if (isMetaMaskProviderAvailable()) {
        detectedWallet = "MetaMask";
      }
    }
  }

  // Enhanced connection check: use hook result OR fallback checks
  // Fallback 1: wagmi is connected and address matches
  // Fallback 2: ownerAddress is set and wallet is detected (temporary state until wagmi updates)
  const isMetaMaskConnectedEnhanced =
    isMetaMaskConnected ||
    (wagmiIsConnected && hasMatchingAddress && detectedWallet === "MetaMask") ||
    (ownerAddress && detectedWallet === "MetaMask" && isMetaMaskAvailable);
  const isPhantomConnectedEnhanced =
    isPhantomConnected ||
    (wagmiIsConnected && hasMatchingAddress && detectedWallet === "Phantom") ||
    (ownerAddress && detectedWallet === "Phantom" && isPhantomAvailable);
  const isOneKeyConnectedEnhanced =
    isOneKeyConnected ||
    (wagmiIsConnected && hasMatchingAddress && detectedWallet === "OneKey") ||
    (ownerAddress && detectedWallet === "OneKey" && isOneKeyAvailable);

  const isOKXEthereumConnectedEnhanced =
    isOKXConnected ||
    (wagmiIsConnected && hasMatchingAddress && detectedWallet === "OKX") ||
    (ownerAddress && detectedWallet === "OKX" && isOKXEthereumAvailable);

  const isBinanceConnectedEnhanced =
    isBinanceConnected ||
    (wagmiIsConnected && hasMatchingAddress && detectedWallet === "Binance") ||
    (ownerAddress && detectedWallet === "Binance" && isBinanceAvailable);

  const isBitgetConnectedEnhanced =
    isBitgetConnected ||
    (wagmiIsConnected && hasMatchingAddress && detectedWallet === "Bitget") ||
    (ownerAddress && detectedWallet === "Bitget" && isBitgetAvailable);

  const isRainbowConnectedEnhanced =
    isRainbowConnected ||
    (wagmiIsConnected && hasMatchingAddress && detectedWallet === "Rainbow") ||
    (ownerAddress && detectedWallet === "Rainbow" && isRainbowAvailable);

  const isWalletConnected =
    isMetaMaskConnectedEnhanced ||
    isPhantomConnectedEnhanced ||
    isOneKeyConnectedEnhanced ||
    isOKXEthereumConnectedEnhanced ||
    isBinanceConnectedEnhanced ||
    isBitgetConnectedEnhanced ||
    isRainbowConnectedEnhanced;

    // Check if Solana is connected (Phantom Solana, OneKey Solana, OKX Solana, or Binance Solana doesn't use wagmi)
  const isSolanaConnected = (() => {
    if (connectionChainType !== "solana") return false;
    
    // Check OneKey Solana provider
    const oneKeySolanaProvider = getOneKeySolanaProvider();
    if (oneKeySolanaProvider && oneKeySolanaProvider.isConnected && oneKeySolanaProvider.publicKey) {
      const solanaAddress = oneKeySolanaProvider.publicKey.toString();
      if (ownerAddress === solanaAddress) {
        return true;
      }
    }
    
    // Check OKX Solana provider
    const okxSolanaProvider = getOKXSolanaProvider();
    if (okxSolanaProvider && okxSolanaProvider.isConnected && okxSolanaProvider.publicKey) {
      const solanaAddress = okxSolanaProvider.publicKey?.toString();
      if (solanaAddress && ownerAddress === solanaAddress) {
        return true;
      }
    }
    
    // Check Binance Solana provider
    const binanceSolanaProvider = getBinanceSolanaProvider();
    if (binanceSolanaProvider && binanceSolanaProvider.isConnected && binanceSolanaProvider.publicKey) {
      const solanaAddress = binanceSolanaProvider.publicKey?.toString();
      if (solanaAddress && ownerAddress === solanaAddress) {
        return true;
      }
    }
    
    // Check Bitget Solana provider
    const bitgetSolanaProvider = getBitgetSolanaProvider();
    if (bitgetSolanaProvider && bitgetSolanaProvider.isConnected && bitgetSolanaProvider.publicKey) {
      const solanaAddress = bitgetSolanaProvider.publicKey?.toString();
      if (solanaAddress && ownerAddress === solanaAddress) {
        return true;
      }
    }
    
    // Check Coinbase Wallet Solana provider
    const coinbaseSolanaProvider = getCoinbaseWalletSolanaProvider();
    if (coinbaseSolanaProvider && coinbaseSolanaProvider.isConnected && coinbaseSolanaProvider.publicKey) {
      const solanaAddress = coinbaseSolanaProvider.publicKey?.toString();
      if (solanaAddress && ownerAddress === solanaAddress) {
        return true;
      }
    }
    
    // Primary check: Phantom provider is connected and address matches
    const solanaProvider = getPhantomSolanaProvider();
    if (solanaProvider && solanaProvider.isConnected && solanaProvider.publicKey) {
      const solanaAddress = solanaProvider.publicKey.toString();
      if (ownerAddress === solanaAddress) {
        return true;
      }
    }
    
    // Fallback: we have ownerAddress and availableAddresses from Solana connection
    // This handles cases where provider state hasn't updated yet but we have the address
    if (ownerAddress && availableAddresses.length > 0) {
      const isSolanaAddress = !ownerAddress.startsWith("0x") && ownerAddress.length >= 32 && ownerAddress.length <= 44;
      const matchesAvailable = availableAddresses.some(
        addr => addr === ownerAddress
      );
      if (isSolanaAddress && matchesAvailable) {
        return true;
      }
    }
    
    return false;
  })();

  // Check if Bitcoin is connected (Bitcoin wallets don't use wagmi)
  const isBitcoinConnected = (() => {
    if (connectionChainType !== "bitcoin") return false;
    if (!ownerAddress) return false;
    // Check if ownerAddress is a Bitcoin address format
    const isBitcoinAddress = /^(bc1|[13]|[mn2])[a-zA-HJ-NP-Z0-9]{25,62}$/.test(ownerAddress);
    if (!isBitcoinAddress) return false;
    // Verify wallet is still available and address matches
    const wallet = getPreferredBitcoinWallet();
    return wallet !== null && isBitcoinWalletAvailable();
  })();

  // Check if Ethereum wallet is connected (via wagmi or direct connection)
  // For Ethereum: we consider it connected if:
  // 1. wagmi says connected AND has address, OR
  // 2. We have ownerAddress from direct connection AND availableAddresses (Phantom/MetaMask/OneKey direct connection)
  const isEthereumConnected = (() => {
    if (connectionChainType !== "ethereum") return false;
    // Primary check: wagmi is connected with address
    if (wagmiIsConnected && wagmiAddress) return true;
    // Fallback: we have addresses from direct wallet connection (before wagmi updates)
    if (ownerAddress && availableAddresses.length > 0) {
      const isEthereumAddress = ownerAddress.startsWith("0x") || ownerAddress.startsWith("0X");
      const matchesAvailable = availableAddresses.some(
        addr => addr.toLowerCase() === ownerAddress.toLowerCase()
      );
      return isEthereumAddress && matchesAvailable;
    }
    return false;
  })();

  // Wallet extension is connected if Ethereum is connected OR Solana is connected OR Bitcoin is connected
  // This ensures we only show "Connected" when a wallet extension is actually connected,
  // not just when ownerAddress exists from localStorage/onboard
  const hasWalletExtensionConnection = isEthereumConnected || isSolanaConnected || isBitcoinConnected;
  
  // Determine connected wallet name based on chain type and connection state
  // Must be defined after isSolanaConnected, isBitcoinConnected, and isEthereumConnected
  const connectedWallet = (() => {
    if (isSolanaConnected) {
      // Check if OneKey Solana is connected
      const oneKeySolanaProvider = getOneKeySolanaProvider();
      if (oneKeySolanaProvider && oneKeySolanaProvider.isConnected && ownerAddress) {
        const oneKeySolanaAddress = oneKeySolanaProvider.publicKey?.toString();
        if (oneKeySolanaAddress === ownerAddress) {
          return "OneKey (Solana)";
        }
      }
      console.log("OKX Solana is checking");
      // Check if OKX Solana is connected
      const okxSolanaProvider = getOKXSolanaProvider();
      if (okxSolanaProvider && okxSolanaProvider.isConnected && ownerAddress) {
        const okxSolanaAddress = okxSolanaProvider.publicKey?.toString();
        if (okxSolanaAddress === ownerAddress) {
          return "OKX (Solana)";
        }
      }
      console.log("Binance Solana is checking");
      // Check if Binance Solana is connected
      const binanceSolanaProvider = getBinanceSolanaProvider();
      if (binanceSolanaProvider && binanceSolanaProvider.isConnected && ownerAddress) {
        const binanceSolanaAddress = binanceSolanaProvider.publicKey?.toString();
        if (binanceSolanaAddress === ownerAddress) {
          return "Binance (Solana)";
        }
      }
      console.log("Coinbase Wallet Solana is checking");
      // Check if Coinbase Wallet Solana is connected
      const coinbaseSolanaProvider = getCoinbaseWalletSolanaProvider();
      if (coinbaseSolanaProvider && coinbaseSolanaProvider.isConnected && ownerAddress) {
        const coinbaseSolanaAddress = coinbaseSolanaProvider.publicKey?.toString();
        if (coinbaseSolanaAddress === ownerAddress) {
          return "Coinbase Wallet (Solana)";
        }
      }
      return "Phantom (Solana)";
    }
    if (isBitcoinConnected) {
      const wallet = getPreferredBitcoinWallet();
      return wallet ? `${wallet.name} (Bitcoin)` : "Bitcoin Wallet";
    }
      if (isEthereumConnected) {
        // Priority: OneKey > OKX > Bitget > Rainbow > Binance > MetaMask > Phantom
        // OneKey provides backward compatibility so must be checked first
        if (isOneKeyConnectedEnhanced || (detectedWallet === "OneKey" && isOneKeyAvailable)) return "OneKey";
        if (isOKXEthereumConnectedEnhanced) return "OKX (Ethereum)";
        if (isBitgetConnectedEnhanced) return "Bitget";
        if (isRainbowConnectedEnhanced) return "Rainbow";
        if (isBinanceConnectedEnhanced) return "Binance";
        if (isMetaMaskConnectedEnhanced) return "MetaMask";
        if (isPhantomConnectedEnhanced) return "Phantom (Ethereum)";
      }
    return null;
  })();
  
  // Wallet is connected if we have addresses available (even if not selected yet)
  // OR if wallet extension is connected
  const hasWalletConnection =
    availableAddresses.length > 0 || hasWalletExtensionConnection;
  // Wallet is ready when extension is connected, has an address, and agent is configured
  // For Ethereum: use wagmiAddress if available, otherwise ownerAddress (for direct connections)
  // For Solana/Bitcoin: use ownerAddress
  const effectiveEthereumAddress = wagmiAddress || (isEthereumConnected && ownerAddress ? ownerAddress : null);
  const isWalletReady = hasWalletExtensionConnection && (effectiveEthereumAddress || ((isSolanaConnected || isBitcoinConnected) && ownerAddress)) && agentAddress;

  // Check if credentials were restored from sessionStorage on mount
  useEffect(() => {
    if (agentAddress && agentPrivateKey) {
      try {
        const storedAddress = sessionStorage.getItem("agent_delegation_address");
        if (storedAddress === agentAddress) {
          setCredentialsRestored(true);
          // Clear indicator after 5 seconds
          const timer = setTimeout(() => setCredentialsRestored(false), 5000);
          return () => clearTimeout(timer);
        }
      } catch (error) {
        // sessionStorage might not be available
        console.warn("Failed to check sessionStorage:", error);
      }
    }
  }, [agentAddress, agentPrivateKey]);

  // Sync ownerAddress with wagmiAddress when Ethereum wallet is connected
  // Only sync for Ethereum connections - Solana/Bitcoin don't use wagmi
  // Use a ref to track the last synced wagmiAddress to prevent infinite loops
  const lastSyncedWagmiAddressRef = useRef<string | undefined>(undefined);
  
  useEffect(() => {
    // Only sync for Ethereum chain type
    if (connectionChainType !== "ethereum") {
      return;
    }

    if (wagmiIsConnected && wagmiAddress) {
      // Only update if wagmiAddress actually changed (not just ownerAddress)
      if (lastSyncedWagmiAddressRef.current !== wagmiAddress) {
        const currentOwnerAddress = ownerAddress?.toLowerCase() || "";
        const currentWagmiAddress = wagmiAddress.toLowerCase();
        if (currentOwnerAddress !== currentWagmiAddress) {
          console.log("[Connection Sync] Updating ownerAddress to match wagmiAddress:", wagmiAddress);
          setOwnerAddress(wagmiAddress);
          lastSyncedWagmiAddressRef.current = wagmiAddress;
        } else {
          // Addresses already match, just update the ref
          lastSyncedWagmiAddressRef.current = wagmiAddress;
        }
      }
    } else if (wagmiIsConnected && !wagmiAddress && ownerAddress && availableAddresses.length > 0) {
      // If wagmi says connected but no address yet, and we have addresses from direct connection
      // This can happen with Phantom - wagmi might be slow to update
      const matchingAddress = availableAddresses.find(
        addr => addr.toLowerCase() === ownerAddress.toLowerCase()
      );
      if (matchingAddress) {
        console.log("[Connection Sync] Wagmi connected but no address yet, keeping ownerAddress:", ownerAddress);
      }
    } else if (!wagmiIsConnected) {
      // Reset ref when disconnected
      lastSyncedWagmiAddressRef.current = undefined;
    }
    // Note: We don't clear ownerAddress on disconnect here because the disconnect handler below handles that
    // Note: We removed ownerAddress from dependencies to prevent infinite loops - we only sync when wagmiAddress changes
  }, [wagmiIsConnected, wagmiAddress, setOwnerAddress, connectionChainType]);

  // Clear addresses when wallet disconnects
  // Only clear if it's an Ethereum connection that disconnected via wagmi
  // Don't clear Solana/Bitcoin connections (they don't use wagmi)
  // Use a ref to track previous wagmiIsConnected state to prevent unnecessary runs
  const prevWagmiIsConnectedRef = useRef<boolean>(wagmiIsConnected);
  
  useEffect(() => {
    // Only clear if:
    // 1. It's Ethereum chain type AND wagmi disconnected (transition from connected to disconnected)
    // 2. We have addresses that are Ethereum addresses
    const wasConnected = prevWagmiIsConnectedRef.current;
    const isNowDisconnected = !wagmiIsConnected;
    
    // Only act on transition from connected to disconnected
    if (connectionChainType === "ethereum" && wasConnected && isNowDisconnected) {
      // Check current state (don't add to dependencies to avoid loops)
      if (availableAddresses.length > 0) {
        const hasEthereumAddress = availableAddresses.some(addr => addr.startsWith("0x") || addr.startsWith("0X"));
        if (hasEthereumAddress) {
          setAvailableAddresses([]);
          if (ownerAddress && (ownerAddress.startsWith("0x") || ownerAddress.startsWith("0X"))) {
            setOwnerAddress("");
          }
        }
      }
    }
    
    // Update ref after checking
    prevWagmiIsConnectedRef.current = wagmiIsConnected;
  }, [wagmiIsConnected, connectionChainType]);

  // Monitor wallet disconnection events from wallet extensions
  useWalletDisconnectDetection({
    onDisconnect: (event: WalletDisconnectEvent) => {
      console.warn(`[Wallet Disconnect] ${event.walletName} ${event.chainType} wallet disconnected:`, event.reason);
      
      // Only clear state if the disconnected wallet matches the current connection
      const shouldClearState = 
        (event.chainType === "ethereum" && connectionChainType === "ethereum") ||
        (event.chainType === "solana" && connectionChainType === "solana") ||
        (event.chainType === "bitcoin" && connectionChainType === "bitcoin");
      
      if (shouldClearState) {
        // Clear wallet addresses and owner address
        setAvailableAddresses([]);
        setOwnerAddress("");
        
        // Show user-friendly alert
        const walletDisplayName = event.walletName === "OKX" ? "OKX Wallet" : 
                                  event.walletName === "Phantom" ? "Phantom Wallet" :
                                  event.walletName === "OneKey" ? "OneKey Wallet" :
                                  event.walletName === "Binance" ? "Binance Wallet" :
                                  event.walletName === "MetaMask" ? "MetaMask" :
                                  "Wallet";
        
        const reasonMessage = event.reason 
          ? `\n\nReason: ${event.reason}` 
          : "";
        
        alert(
          `${walletDisplayName} has disconnected from the ${event.chainType} network.${reasonMessage}\n\n` +
          "Please reconnect your wallet to continue."
        );
      }
    },
    enableOKX: true,
    enablePhantom: true,
    enableOneKey: true,
    enableEthereum: true,
  });

  // Debug: Log connection state
  // Only log when key values actually change, not on every render
  // Use a ref to track previous values and only log on changes
  const prevDebugRef = useRef<{
    wagmiIsConnected: boolean;
    wagmiAddress: string | undefined;
    ownerAddress: string;
    connectionChainType: string;
    availableAddressesLength: number;
  }>({
    wagmiIsConnected: false,
    wagmiAddress: undefined,
    ownerAddress: "",
    connectionChainType: "",
    availableAddressesLength: 0,
  });

  useEffect(() => {
    const prev = prevDebugRef.current;
    const hasChanged = 
      prev.wagmiIsConnected !== wagmiIsConnected ||
      prev.wagmiAddress !== wagmiAddress ||
      prev.ownerAddress !== ownerAddress ||
      prev.connectionChainType !== connectionChainType ||
      prev.availableAddressesLength !== availableAddresses.length;

    if (hasChanged) {
      console.log("[Connection Debug] wagmiIsConnected:", wagmiIsConnected);
      console.log("[Connection Debug] wagmiAddress:", wagmiAddress);
      console.log("[Connection Debug] ownerAddress:", ownerAddress);
      console.log("[Connection Debug] connectionChainType:", connectionChainType);
      console.log("[Connection Debug] availableAddresses:", availableAddresses);
      console.log("[Connection Debug] isPhantomConnected (hook):", isPhantomConnected);
      console.log("[Connection Debug] isMetaMaskConnected (hook):", isMetaMaskConnected);
      console.log("[Connection Debug] isOneKeyConnected (hook):", isOneKeyConnected);
      console.log("[Connection Debug] isOKXConnected (hook):", isOKXConnected);
      console.log("[Connection Debug] isBinanceConnected (hook):", isBinanceConnected);
      console.log("[Connection Debug] isBitgetConnected (hook):", isBitgetConnected);
      console.log("[Connection Debug] isRainbowConnected (hook):", isRainbowConnected);
      console.log("[Connection Debug] detectedWallet:", detectedWallet);
      console.log("[Connection Debug] hasMatchingAddress:", hasMatchingAddress);
      console.log("[Connection Debug] isPhantomConnectedEnhanced:", isPhantomConnectedEnhanced);
      console.log("[Connection Debug] isWalletConnected:", isWalletConnected);
      console.log("[Connection Debug] isSolanaConnected:", isSolanaConnected);
      console.log("[Connection Debug] isBitcoinConnected:", isBitcoinConnected);
      console.log("[Connection Debug] isEthereumConnected:", isEthereumConnected);
      console.log("[Connection Debug] hasWalletExtensionConnection:", hasWalletExtensionConnection);
      console.log("[Connection Debug] hasWalletConnection:", hasWalletConnection);
      
      // Update ref
      prevDebugRef.current = {
        wagmiIsConnected,
        wagmiAddress,
        ownerAddress: ownerAddress || "",
        connectionChainType,
        availableAddressesLength: availableAddresses.length,
      };
    }
  }, [
    wagmiIsConnected,
    wagmiAddress,
    ownerAddress,
    connectionChainType,
    availableAddresses.length,
    // Only include hook values, not computed values that change on every render
    isPhantomConnected,
    isMetaMaskConnected,
    isOneKeyConnected,
    isOKXConnected,
    isBinanceConnected,
    isBitgetConnected,
  ]);

  const handleConnectMetaMask = async () => {
    if (!isMetaMaskAvailable) {
      alert(
        "MetaMask wallet is not installed. Please install MetaMask wallet extension.",
      );
      return;
    }

    try {
      // Request accounts from MetaMask first
      const ethereum = (window as any).ethereum;
      if (ethereum) {
        const accounts = await ethereum.request({
          method: "eth_requestAccounts",
        });

        // Store all available addresses (filter out invalid values)
        if (accounts && accounts.length > 0) {
          const validAddresses = accounts.filter((addr: any): addr is string => 
            addr && typeof addr === "string" && addr.trim().length > 0
          );
          if (validAddresses.length > 0) {
            setAvailableAddresses(validAddresses);
            // If only one address, auto-select it
            if (validAddresses.length === 1) {
              setOwnerAddress(validAddresses[0]);
            }
          }
        }
      }

      // Connect via wagmi
      if (injectedConnector) {
        await connect({ connector: injectedConnector });
        // Wait for wagmi to update
        await new Promise((resolve) => setTimeout(resolve, 300));
      } else {
        alert(
          "MetaMask connector not found. Please ensure MetaMask wallet is installed.",
        );
      }
    } catch (error: any) {
      if (error.code === 4001) {
        alert("User rejected the connection request.");
      } else {
        console.error("Error connecting to MetaMask:", error);
        alert(
          `Failed to connect to MetaMask: ${error.message || "Unknown error"}`,
        );
      }
    }
  };

  const handleConnectPhantom = async () => {
    console.log("[Phantom Debug] Starting connection process...");
    console.log("[Phantom Debug] Connection chain type:", connectionChainType);
    console.log("[Phantom Debug] isPhantomAvailable:", isPhantomAvailable);
    console.log("[Phantom Debug] window.phantom:", (window as any).phantom);
    console.log("[Phantom Debug] window.ethereum:", (window as any).ethereum);
    console.log(
      "[Phantom Debug] window.ethereum?.isPhantom:",
      (window as any).ethereum?.isPhantom,
    );

    // Handle Solana connection
    if (connectionChainType === "solana") {
      try {
        if (!isPhantomSolanaAvailable()) {
          alert(
            "Phantom Solana wallet is not available. Please ensure Phantom wallet is installed and unlocked.",
          );
          return;
        }

        console.log("[Phantom Debug] Connecting to Solana...");
        const solanaAddress = await connectPhantomSolana();
        console.log("[Phantom Debug] Solana address received:", solanaAddress);

        if (solanaAddress && typeof solanaAddress === "string") {
          setAvailableAddresses([solanaAddress]);
          setOwnerAddress(solanaAddress);
          console.log("[Phantom Debug] Solana connection successful");
          console.log("[Phantom Debug] Solana address set:", solanaAddress);
          console.log("[Phantom Debug] availableAddresses set:", [solanaAddress]);
          
          // Verify provider state
          const provider = getPhantomSolanaProvider();
          console.log("[Phantom Debug] Solana provider state:", {
            exists: !!provider,
            isConnected: provider?.isConnected,
            publicKey: provider?.publicKey?.toString(),
          });
        }
      } catch (error: any) {
        console.error("[Phantom Debug] Solana connection error:", error);
        if (error.code === 4001 || error.message?.includes("rejected")) {
          alert("User rejected the Solana connection request.");
        } else {
          alert(
            `Failed to connect to Phantom Solana: ${error.message || "Unknown error"}`,
          );
        }
      }
      return;
    }

    // Handle Ethereum connection (existing logic)
    if (!isPhantomAvailable) {
      console.error("[Phantom Debug] Phantom wallet not available");
      alert(
        "Phantom wallet is not installed. Please install Phantom wallet extension.",
      );
      return;
    }

    try {
      // Phantom wallet can expose its provider in different ways:
      // 1. window.phantom.ethereum (most common for Ethereum)
      // 2. window.phantom itself (some versions)
      // 3. window.ethereum with isPhantom flag
      let phantomProvider = (window as any).phantom?.ethereum;
      console.log(
        "[Phantom Debug] phantomProvider (window.phantom.ethereum):",
        phantomProvider,
      );

      // Check if Phantom is in Solana mode (ethereum provider might be null)
      const hasPhantomSolana = !!(window as any).phantom?.solana || !!(window as any).solana;
      const hasPhantomEthereum = !!(window as any).phantom?.ethereum || !!(window as any).ethereum?.isPhantom;
      
      // If Phantom exists but Ethereum provider is not available, user might be in Solana mode
      if ((window as any).phantom && !phantomProvider && !(window as any).ethereum?.isPhantom) {
        console.warn("[Phantom Debug] Phantom detected but Ethereum provider not available");
        console.warn("[Phantom Debug] Phantom might be in Solana mode");
        
        // Check if we can detect Solana mode
        if (hasPhantomSolana && !hasPhantomEthereum) {
          alert(
            "Phantom wallet is currently in Solana mode. Please switch to Ethereum mode in your Phantom wallet extension to connect.\n\n" +
            "To switch:\n" +
            "1. Open your Phantom wallet extension\n" +
            "2. Click on the network selector (top of the wallet)\n" +
            "3. Switch from Solana to Ethereum\n" +
            "4. Then try connecting again."
          );
          return;
        }
      }

      // Check if window.phantom itself is the provider (has request method)
      if (!phantomProvider && (window as any).phantom) {
        const phantom = (window as any).phantom;
        if (typeof phantom.request === "function") {
          phantomProvider = phantom;
          console.log("[Phantom Debug] Using window.phantom as provider");
        }
      }

      // Fallback to window.ethereum if it's Phantom
      if (!phantomProvider && (window as any).ethereum?.isPhantom) {
        phantomProvider = (window as any).ethereum;
        console.log(
          "[Phantom Debug] Using window.ethereum as provider (isPhantom=true)",
        );
      }

      if (!phantomProvider) {
        console.error("[Phantom Debug] No valid Phantom provider found");
        console.error("[Phantom Debug] Available providers:", {
          hasWindowPhantom: !!(window as any).phantom,
          hasPhantomEthereum: !!(window as any).phantom?.ethereum,
          hasPhantomSolana: hasPhantomSolana,
          phantomHasRequest:
            typeof (window as any).phantom?.request === "function",
          hasWindowEthereum: !!(window as any).ethereum,
          ethereumIsPhantom: (window as any).ethereum?.isPhantom,
          ethereumIsMetaMask: (window as any).ethereum?.isMetaMask,
        });
        
        // Provide helpful error message
        if ((window as any).phantom && hasPhantomSolana) {
          alert(
            "Phantom wallet is in Solana mode. Please switch to Ethereum mode:\n\n" +
            "1. Open your Phantom wallet extension\n" +
            "2. Switch from Solana to Ethereum network\n" +
            "3. Then try connecting again."
          );
        } else {
          alert(
            "Phantom wallet Ethereum provider not found. Please ensure Phantom wallet is installed and switched to Ethereum mode.",
          );
        }
        return;
      }

      console.log("[Phantom Debug] Using provider, requesting accounts...");
      // Request account access from Phantom directly
      // This will trigger the Phantom wallet popup
      let accounts;
      try {
        accounts = await phantomProvider.request({
          method: "eth_requestAccounts",
        });
      } catch (ethError: any) {
        // Handle case where Phantom is in Solana mode or Ethereum is not available
        console.error("[Phantom Debug] Error requesting Ethereum accounts:", ethError);
        
        // Check error message for Solana-related issues
        const errorMessage = ethError?.message?.toLowerCase() || "";
        const errorCode = ethError?.code;
        
        if (
          errorCode === 4001 ||
          errorMessage.includes("user rejected") ||
          errorMessage.includes("rejected")
        ) {
          alert("User rejected the connection request.");
          return;
        }
        
        // Check if Phantom might be in Solana mode
        if (
          errorMessage.includes("solana") ||
          errorMessage.includes("ethereum") ||
          !phantomProvider ||
          errorCode === -32602 || // Invalid params
          errorCode === -32603    // Internal error
        ) {
          alert(
            "Unable to connect to Phantom Ethereum. Please ensure:\n\n" +
            "1. Phantom wallet is open and unlocked\n" +
            "2. Phantom is switched to Ethereum mode (not Solana)\n" +
            "3. You have an Ethereum account in Phantom\n\n" +
            "To switch to Ethereum mode:\n" +
            "- Open Phantom extension\n" +
            "- Click the network selector at the top\n" +
            "- Select Ethereum network"
          );
          return;
        }
        
        // Re-throw if it's an unexpected error
        throw ethError;
      }
      console.log("[Phantom Debug] Accounts received:", accounts);

      // Store all available addresses
      if (accounts && accounts.length > 0) {
        setAvailableAddresses(accounts);
        console.log("[Phantom Debug] Available addresses:", accounts);

        // If only one address, auto-select it
        if (accounts.length === 1) {
          console.log(
            "[Phantom Debug] Auto-selecting single address:",
            accounts[0],
          );
          setOwnerAddress(accounts[0]);
        }
      }

      // After Phantom is connected, connect via wagmi
      // Small delay to ensure Phantom has processed the connection
      console.log("[Phantom Debug] Waiting 200ms before wagmi connection...");
      await new Promise((resolve) => setTimeout(resolve, 200));

      console.log("[Phantom Debug] injectedConnector:", injectedConnector);
      console.log("[Phantom Debug] All connectors:", connectors);
      console.log("[Phantom Debug] Current wagmi state - isConnected:", wagmiIsConnected, "address:", wagmiAddress);
      console.log("[Phantom Debug] Current ownerAddress:", ownerAddress);

      if (injectedConnector) {
        console.log(
          "[Phantom Debug] Connecting via wagmi injected connector...",
        );
        try {
          const connectResult = await connect({ connector: injectedConnector });
          console.log("[Phantom Debug] Wagmi connect() result:", connectResult);
          console.log("[Phantom Debug] Wagmi connect() called");

          // Wait longer for wagmi to update its state
          // Note: wagmi hooks update asynchronously, so we wait and let the useEffect handle sync
          console.log("[Phantom Debug] Waiting for wagmi state update...");
          await new Promise((resolve) => setTimeout(resolve, 500));
          
          // Force a small delay to allow React to re-render with updated wagmi state
          await new Promise((resolve) => setTimeout(resolve, 300));
          
          console.log("[Phantom Debug] After wait - wagmi state - isConnected:", wagmiIsConnected, "address:", wagmiAddress);
          console.log("[Phantom Debug] After wait - ownerAddress:", ownerAddress);
          
          // If wagmi hasn't updated but we have ownerAddress, that's fine
          // The sync useEffect will handle updating ownerAddress when wagmi updates
          // For now, we ensure ownerAddress is set from Phantom accounts
          if (!wagmiAddress && ownerAddress) {
            console.log("[Phantom Debug] Wagmi not updated yet, but ownerAddress is set from Phantom");
            console.log("[Phantom Debug] The sync effect will update when wagmi is ready");
          } else if (wagmiAddress) {
            console.log("[Phantom Debug] Wagmi address updated successfully:", wagmiAddress);
            // Ensure ownerAddress matches wagmiAddress
            if (ownerAddress?.toLowerCase() !== wagmiAddress.toLowerCase()) {
              console.log("[Phantom Debug] Syncing ownerAddress to match wagmiAddress");
              setOwnerAddress(wagmiAddress);
            }
          }
        } catch (wagmiError: any) {
          console.error("[Phantom Debug] Error connecting via wagmi:", wagmiError);
          // Even if wagmi fails, we have the address from Phantom
          // So we can continue with ownerAddress
          console.warn("[Phantom Debug] Continuing with Phantom address despite wagmi error");
          console.warn("[Phantom Debug] ownerAddress from Phantom:", ownerAddress);
        }
      } else {
        console.error("[Phantom Debug] Injected connector not found");
        // Even without wagmi connector, we have the address from Phantom
        console.warn("[Phantom Debug] No wagmi connector, but we have Phantom address:", ownerAddress);
      }
    } catch (error: any) {
      console.error("[Phantom Debug] Error caught:", error);
      console.error("[Phantom Debug] Error code:", error.code);
      console.error("[Phantom Debug] Error message:", error.message);
      console.error("[Phantom Debug] Error stack:", error.stack);

      if (error.code === 4001) {
        console.log("[Phantom Debug] User rejected the connection");
        alert("User rejected the connection request.");
      } else {
        console.error("[Phantom Debug] Unknown error during connection");
        alert(
          `Failed to connect to Phantom Ethereum: ${error.message || "Unknown error"}\n\n` +
          "Please ensure Phantom wallet is unlocked and switched to Ethereum mode."
        );
      }
    }
  };

  const handleConnectBitcoin = async () => {
    // Check if OKX wallet is installed (even if Bitcoin provider not immediately available)
    const hasOKXWallet = isOKXWalletInstalled();
    const hasBitcoinWallets = isBitcoinWalletAvailable();
    
    if (!hasBitcoinWallets && !hasOKXWallet) {
      alert(
        "No Bitcoin wallet detected. Please install OneKey, OKX, Bitget, Unisat, or Xverse wallet extension.\n\n" +
        "If you have OKX or Bitget installed, ensure it's switched to Bitcoin network in the extension."
      );
      return;
    }

    // If OKX is installed but Bitcoin provider not available, guide user
    if (hasOKXWallet && !hasBitcoinWallets) {
      alert(
        "OKX wallet detected, but Bitcoin provider is not available.\n\n" +
        "Please ensure:\n" +
        "1. OKX wallet extension is unlocked\n" +
        "2. OKX wallet is switched to Bitcoin network (not Ethereum)\n" +
        "3. You have a Bitcoin account in OKX\n\n" +
        "After switching to Bitcoin network in OKX, refresh this page and try again."
      );
      return;
    }

    try {
      console.log("[Bitcoin Debug] Starting Bitcoin connection...");
      
      // Use OKX SDK if OKX wallet is available and preferred
      let bitcoinAddress: string;
      if (hasOKXWallet && isOKXBitcoinSDKAvailable()) {
        console.log("[Bitcoin Debug] Using OKX SDK for Bitcoin connection");
        bitcoinAddress = await getOKXBitcoinAddress();
      } else {
        bitcoinAddress = await getBitcoinAddress();
      }
      
      console.log("[Bitcoin Debug] Bitcoin address received:", bitcoinAddress);

      if (bitcoinAddress && typeof bitcoinAddress === "string") {
        setAvailableAddresses([bitcoinAddress]);
        setOwnerAddress(bitcoinAddress);
        console.log("[Bitcoin Debug] Bitcoin connection successful");
      }
    } catch (error: any) {
      console.error("[Bitcoin Debug] Bitcoin connection error:", error);
      if (error.code === 4001 || error.message?.includes("rejected") || error.message?.includes("User rejected")) {
        alert("User rejected the Bitcoin wallet connection request.");
      } else {
        const wallet = getPreferredBitcoinWallet();
        const walletName = wallet?.name || "Bitcoin wallet";
        const errorMessage = error.message || "Unknown error";
        
        // Provide helpful guidance for OKX
        if (hasOKXWallet && errorMessage.includes("not found") || errorMessage.includes("No Bitcoin wallet")) {
          alert(
            `Failed to connect to ${walletName}: ${errorMessage}\n\n` +
            "If using OKX wallet:\n" +
            "1. Ensure OKX extension is unlocked\n" +
            "2. Switch OKX to Bitcoin network in the extension\n" +
            "3. Make sure you have a Bitcoin account\n" +
            "4. Refresh this page and try again"
          );
        } else {
          alert(
            `Failed to connect to ${walletName}: ${errorMessage}\n\n` +
            "Please ensure your Bitcoin wallet is unlocked and try again."
          );
        }
      }
    }
  };

  const handleConnectOneKey = async () => {
    // Handle Solana connection
    if (connectionChainType === "solana") {
      try {
        if (!isOneKeySolanaAvailable) {
          alert(
            "OneKey Solana wallet is not available. Please ensure OneKey wallet is installed and unlocked.",
          );
          return;
        }

        console.log("[OneKey Debug] Connecting to Solana...");
        const solanaAddress = await connectOneKeySolana();
        console.log("[OneKey Debug] Solana address received:", solanaAddress);

        if (solanaAddress && typeof solanaAddress === "string") {
          setAvailableAddresses([solanaAddress]);
          setOwnerAddress(solanaAddress);
          console.log("[OneKey Debug] Solana connection successful");
        }
      } catch (error: any) {
        console.error("[OneKey Debug] Solana connection error:", error);
        if (error.code === 4001 || error.message?.includes("rejected")) {
          alert("User rejected the Solana connection request.");
        } else {
          alert(
            `Failed to connect to OneKey Solana: ${error.message || "Unknown error"}`,
          );
        }
      }
      return;
    }

    // Handle Bitcoin connection
    if (connectionChainType === "bitcoin") {
      try {
        if (!isOneKeyBitcoinAvailable) {
          alert(
            "OneKey Bitcoin wallet is not available. Please ensure OneKey wallet is installed and switched to Bitcoin network.",
          );
          return;
        }

        console.log("[OneKey Debug] Connecting to Bitcoin...");
        const bitcoinAddress = await getOneKeyBitcoinAddress();
        console.log("[OneKey Debug] Bitcoin address received:", bitcoinAddress);

        if (bitcoinAddress && typeof bitcoinAddress === "string") {
          setAvailableAddresses([bitcoinAddress]);
          setOwnerAddress(bitcoinAddress);
          console.log("[OneKey Debug] Bitcoin connection successful");
        }
      } catch (error: any) {
        console.error("[OneKey Debug] Bitcoin connection error:", error);
        if (error.code === 4001 || error.message?.includes("rejected")) {
          alert("User rejected the Bitcoin connection request.");
        } else {
          const errorMessage = error.message || "Unknown error";
          alert(
            `Failed to connect to OneKey Bitcoin: ${errorMessage}\n\n` +
            "Please ensure your OneKey wallet is unlocked and switched to Bitcoin network."
          );
        }
      }
      return;
    }

    // Handle Ethereum connection (existing logic)
    if (!isOneKeyAvailable) {
      alert(
        "OneKey wallet is not installed. Please install OneKey wallet extension.",
      );
      return;
    }

    try {
      // OneKey wallet exposes its provider at window.$onekey.ethereum
      // We need to request connection directly from OneKey first
      const oneKeyProvider =
        (window as any).$onekey?.ethereum || (window as any).ethereum;

      if (!oneKeyProvider) {
        alert(
          "OneKey wallet provider not found. Please ensure OneKey wallet is installed and enabled.",
        );
        return;
      }

      // Request account access from OneKey directly
      // This will trigger the OneKey wallet popup
      const accounts = await oneKeyProvider.request({
        method: "eth_requestAccounts",
      });

      // Store all available addresses
      if (accounts && accounts.length > 0) {
        const validAddresses = accounts.filter((addr: any) => addr && typeof addr === "string") as string[];
        if (validAddresses.length > 0) {
          setAvailableAddresses(validAddresses);
          // If only one address, auto-select it
          if (validAddresses.length === 1) {
            setOwnerAddress(validAddresses[0]);
          }
        }
      }

      // After OneKey is connected, connect via wagmi
      if (injectedConnector) {
        await connect({ connector: injectedConnector });
        // Wait for wagmi to update
        await new Promise((resolve) => setTimeout(resolve, 300));
      } else {
        alert(
          "OneKey connector not found. Please ensure OneKey wallet is installed.",
        );
      }
    } catch (error: any) {
      if (error.code === 4001) {
        alert("User rejected the connection request.");
      } else {
        console.error("Error connecting to OneKey:", error);
        alert(
          `Failed to connect to OneKey: ${error.message || "Unknown error"}`,
        );
      }
    }
  };

  const handleConnectBinance = async () => {
    // Handle Solana connection
    if (connectionChainType === "solana") {
      try {
        if (!isBinanceSolanaAvailable()) {
          alert(
            "Binance Solana wallet is not available. Please ensure Binance wallet is installed and unlocked.",
          );
          return;
        }

        console.log("[Binance Debug] Connecting to Solana...");
        const solanaAddress = await connectBinanceSolana();
        console.log("[Binance Debug] Solana address received:", solanaAddress);

        if (solanaAddress && typeof solanaAddress === "string") {
          setAvailableAddresses([solanaAddress]);
          setOwnerAddress(solanaAddress);
          console.log("[Binance Debug] Solana connection successful");
        }
      } catch (error: any) {
        console.error("[Binance Debug] Solana connection error:", error);
        if (error.code === 4001 || error.message?.includes("rejected")) {
          alert("User rejected the Solana connection request.");
        } else {
          alert(
            `Failed to connect to Binance Solana: ${error.message || "Unknown error"}`,
          );
        }
      }
      return;
    }

    // Handle Ethereum connection
    // Check availability with polling if needed
    if (!isBinanceAvailable) {
      console.log("[Binance Debug] Not immediately available, checking with polling...");
      const detected: boolean = await waitForBinanceWallet(3000, 100);
      if (detected) {
        setIsBinanceAvailable(true);
        console.log("[Binance Debug] Binance wallet detected via polling!");
      } else {
        alert(
          "Binance wallet is not installed. Please install Binance wallet extension.",
        );
        return;
      }
    }

    try {
      // Use the helper function to get Binance provider (with inpage detection)
      // This handles:
      // 1. Modern: window.binancew3w.isExtension -> window.ethereum
      // 2. Legacy: window.BinanceChain
      // 3. Fallback: window.ethereum with Binance-specific properties
      let binanceProvider = getBinanceProvider();
      
      console.log("[Binance Debug] Initial provider check:", binanceProvider ? "Found" : "Not found");
      console.log("[Binance Debug] window.binancew3w:", (window as any).binancew3w);
      console.log("[Binance Debug] window.BinanceChain:", (window as any).BinanceChain);

      // If not immediately available, wait for it with polling
      if (!binanceProvider) {
        console.log("[Binance Debug] Provider not immediately available, waiting...");
        const detected: boolean = await waitForBinanceWallet(3000, 100);
        if (detected) {
          binanceProvider = getBinanceProvider();
          setIsBinanceAvailable(true);
          console.log("[Binance Debug] Provider found after polling:", binanceProvider ? "Yes" : "No");
        }
      }

      if (!binanceProvider) {
        alert(
          "Binance wallet provider not found. Please ensure Binance wallet is installed and enabled.",
        );
        return;
      }

      // Request account access from Binance directly
      // This will trigger the Binance wallet popup
      const accounts = await binanceProvider.request({
        method: "eth_requestAccounts",
      });

      // Store all available addresses
      if (accounts && accounts.length > 0) {
        const validAddresses = accounts.filter((addr: any): addr is string => 
          addr && typeof addr === "string" && addr.trim().length > 0
        );
        if (validAddresses.length > 0) {
          setAvailableAddresses(validAddresses);
          // If only one address, auto-select it
          if (validAddresses.length === 1) {
            setOwnerAddress(validAddresses[0]);
          }
        }
      }

      // After Binance is connected, connect via wagmi
      if (injectedConnector) {
        await connect({ connector: injectedConnector });
        // Wait for wagmi to update
        await new Promise((resolve) => setTimeout(resolve, 300));
      } else {
        alert(
          "Binance connector not found. Please ensure Binance wallet is installed.",
        );
      }
    } catch (error: any) {
      if (error.code === 4001) {
        alert("User rejected the connection request.");
      } else {
        console.error("Error connecting to Binance:", error);
        alert(
          `Failed to connect to Binance: ${error.message || "Unknown error"}`,
        );
      }
    }
  };

  const handleConnectBitget = async () => {
    // Handle Solana connection
    if (connectionChainType === "solana") {
      try {
        if (!isBitgetSolanaAvailable()) {
          alert(
            "Bitget Solana wallet is not available. Please ensure Bitget wallet is installed and unlocked.",
          );
          return;
        }

        console.log("[Bitget Debug] Connecting to Solana...");
        const solanaAddress = await connectBitgetSolana();
        console.log("[Bitget Debug] Solana address received:", solanaAddress);

        if (solanaAddress && typeof solanaAddress === "string") {
          setAvailableAddresses([solanaAddress]);
          setOwnerAddress(solanaAddress);
          console.log("[Bitget Debug] Solana connection successful");
        }
      } catch (error: any) {
        console.error("[Bitget Debug] Solana connection error:", error);
        if (error.code === 4001 || error.message?.includes("rejected")) {
          alert("User rejected the Solana connection request.");
        } else {
          alert(
            `Failed to connect to Bitget Solana: ${error.message || "Unknown error"}`,
          );
        }
      }
      return;
    }

    // Handle Bitcoin connection
    if (connectionChainType === "bitcoin") {
      try {
        // Check if Bitget Bitcoin (unisat) is available
        // Per analysis: Bitget uses window.bitkeep.unisat (primary) or may shim window.unisat
        const bitkeep = (window as any).bitkeep;
        const hasBitgetWallet = !!(bitkeep?.isBitKeep || bitkeep?.ethereum?.isBitEthereum);
        
        // Primary: Check window.bitkeep.unisat (Bitget's native Bitcoin provider)
        // Secondary: Check window.unisat shim (only if Bitget is detected via other means)
        const unisatProvider = bitkeep?.unisat || 
          (hasBitgetWallet && !(window as any).$onekey && (window as any).unisat);
        
        if (!unisatProvider) {
          alert(
            "Bitget Bitcoin wallet is not available. Please ensure:\n" +
            "1. Bitget wallet extension is unlocked\n" +
            "2. Bitget wallet is switched to Bitcoin network\n" +
            "3. You have a Bitcoin account in Bitget\n\n" +
            "After switching to Bitcoin network in Bitget, refresh this page and try again.",
          );
          return;
        }

        console.log("[Bitget Debug] Connecting to Bitcoin...");
        
        // Request accounts from Bitget's unisat provider
        // Per analysis: window.bitkeep.unisat.requestAccounts() or window.bitkeep.unisat.setIdenties()
        const accounts = await unisatProvider.requestAccounts();
        
        if (accounts && accounts.length > 0) {
          const address = accounts[0];
          setAvailableAddresses(accounts);
          setOwnerAddress(address);
          console.log("[Bitget Debug] Bitcoin connection successful:", address);
        } else {
          throw new Error("No accounts returned from Bitget Bitcoin wallet");
        }
      } catch (error: any) {
        console.error("[Bitget Debug] Bitcoin connection error:", error);
        if (error.code === 4001 || error.message?.includes("rejected") || error.message?.includes("User rejected")) {
          alert("User rejected the Bitcoin connection request.");
        } else {
          alert(
            `Failed to connect to Bitget Bitcoin: ${error.message || "Unknown error"}\n\n` +
            "Please ensure:\n" +
            "1. Bitget wallet extension is unlocked\n" +
            "2. Bitget wallet is switched to Bitcoin network (not Ethereum)\n" +
            "3. You have a Bitcoin account in Bitget\n\n" +
            "After switching to Bitcoin network in Bitget, refresh this page and try again.",
          );
        }
      }
      return;
    }

    // Handle Ethereum connection
    // Check availability
    if (!isBitgetAvailable) {
      console.log("[Bitget Debug] Not immediately available, checking with polling...");
      const detected: boolean = await waitForBitgetWallet(3000, 100);
      if (detected) {
        console.log("[Bitget Debug] Bitget wallet detected via polling!");
      } else {
        alert(
          "Bitget wallet is not installed. Please install Bitget wallet extension.",
        );
        return;
      }
    }

    try {
      // Use the helper function to get Bitget provider
      let bitgetProvider = getBitgetProvider();
      
      console.log("[Bitget Debug] Initial provider check:", bitgetProvider ? "Found" : "Not found");
      console.log("[Bitget Debug] window.bitkeep:", (window as any).bitkeep);

      // If not immediately available, wait for it with polling
      if (!bitgetProvider) {
        console.log("[Bitget Debug] Provider not immediately available, waiting...");
        const detected: boolean = await waitForBitgetWallet(3000, 100);
        if (detected) {
          bitgetProvider = getBitgetProvider();
          console.log("[Bitget Debug] Provider found after polling:", bitgetProvider ? "Yes" : "No");
        }
      }

      if (!bitgetProvider) {
        alert(
          "Bitget wallet provider not found. Please ensure Bitget wallet is installed and enabled.",
        );
        return;
      }

      // Request account access from Bitget directly
      // This will trigger the Bitget wallet popup
      const accounts = await bitgetProvider.request({
        method: "eth_requestAccounts",
      });

      // Store all available addresses
      if (accounts && accounts.length > 0) {
        const validAddresses = accounts.filter((addr: any): addr is string => 
          addr && typeof addr === "string" && addr.trim().length > 0
        );
        if (validAddresses.length > 0) {
          setAvailableAddresses(validAddresses);
          // If only one address, auto-select it
          if (validAddresses.length === 1) {
            setOwnerAddress(validAddresses[0]);
          }
        }
      }

      // After Bitget is connected, connect via wagmi
      if (injectedConnector) {
        await connect({ connector: injectedConnector });
        // Wait for wagmi to update
        await new Promise((resolve) => setTimeout(resolve, 300));
      } else {
        alert(
          "Bitget connector not found. Please ensure Bitget wallet is installed.",
        );
      }
    } catch (error: any) {
      if (error.code === 4001) {
        alert("User rejected the connection request.");
      } else {
        console.error("Error connecting to Bitget:", error);
        alert(
          `Failed to connect to Bitget: ${error.message || "Unknown error"}`,
        );
      }
    }
  };

  const handleConnectTokenPocket = async () => {
    // Handle Solana connection
    if (connectionChainType === "solana") {
      try {
        if (!isTokenPocketSolanaAvailable()) {
          alert(
            "TokenPocket Solana wallet is not available. Please ensure TokenPocket wallet is installed and unlocked.",
          );
          return;
        }

        console.log("[TokenPocket Debug] Connecting to Solana...");
        const solanaAddress = await connectTokenPocketSolana();
        console.log("[TokenPocket Debug] Solana address received:", solanaAddress);

        if (solanaAddress && typeof solanaAddress === "string") {
          setAvailableAddresses([solanaAddress]);
          setOwnerAddress(solanaAddress);
          console.log("[TokenPocket Debug] Solana connection successful");
        }
      } catch (error: any) {
        console.error("[TokenPocket Debug] Solana connection error:", error);
        if (error.code === 4001 || error.message?.includes("rejected")) {
          alert("User rejected the Solana connection request.");
        } else {
          alert(
            `Failed to connect to TokenPocket Solana: ${error.message || "Unknown error"}`,
          );
        }
      }
      return;
    }


    // Handle Ethereum connection
    // Check availability
    if (!isTokenPocketAvailable) {
      console.log("[TokenPocket Debug] Not immediately available, checking with polling...");
      const detected: boolean = await waitForTokenPocketWallet(3000, 100);
      if (detected) {
        console.log("[TokenPocket Debug] TokenPocket wallet detected via polling!");
      } else {
        alert(
          "TokenPocket wallet is not installed. Please install TokenPocket wallet extension.",
        );
        return;
      }
    }

    try {
      // Use the helper function to get TokenPocket provider
      let tokenPocketProvider = getTokenPocketProvider();
      
      console.log("[TokenPocket Debug] Initial provider check:", tokenPocketProvider ? "Found" : "Not found");
      console.log("[TokenPocket Debug] window.ethereum.isTokenPocket:", (window as any).ethereum?.isTokenPocket);

      // If not immediately available, wait for it with polling
      if (!tokenPocketProvider) {
        console.log("[TokenPocket Debug] Provider not immediately available, waiting...");
        const detected: boolean = await waitForTokenPocketWallet(3000, 100);
        if (detected) {
          tokenPocketProvider = getTokenPocketProvider();
          console.log("[TokenPocket Debug] Provider found after polling:", tokenPocketProvider ? "Yes" : "No");
        }
      }

      if (!tokenPocketProvider) {
        alert(
          "TokenPocket wallet provider not found. Please ensure TokenPocket wallet is installed and enabled.",
        );
        return;
      }

      // Request account access from TokenPocket directly
      // This will trigger the TokenPocket wallet popup
      const accounts = await tokenPocketProvider.request({
        method: "eth_requestAccounts",
      });

      // Store all available addresses
      if (accounts && accounts.length > 0) {
        const validAddresses = accounts.filter((addr: any): addr is string => 
          addr && typeof addr === "string" && addr.trim().length > 0
        );
        if (validAddresses.length > 0) {
          setAvailableAddresses(validAddresses);
          // If only one address, auto-select it
          if (validAddresses.length === 1) {
            setOwnerAddress(validAddresses[0]);
          }
        }
      }

      // After TokenPocket is connected, connect via wagmi
      if (injectedConnector) {
        await connect({ connector: injectedConnector });
        // Wait for wagmi to update
        await new Promise((resolve) => setTimeout(resolve, 300));
      } else {
        alert(
          "TokenPocket connector not found. Please ensure TokenPocket wallet is installed.",
        );
      }
    } catch (error: any) {
      if (error.code === 4001) {
        alert("User rejected the connection request.");
      } else {
        console.error("Error connecting to TokenPocket:", error);
        alert(
          `Failed to connect to TokenPocket: ${error.message || "Unknown error"}`,
        );
      }
    }
  };

  const handleConnectRainbow = async () => {
    // Rainbow Wallet only supports Ethereum
    if (connectionChainType !== "ethereum") {
      alert(
        "Rainbow Wallet only supports Ethereum network. Please switch to Ethereum chain type.",
      );
      return;
    }

    // Check availability
    if (!isRainbowAvailable) {
      alert(
        "Rainbow Wallet is not installed. Please install Rainbow Wallet extension.",
      );
      return;
    }

    try {
      const ethereum = (window as any).ethereum;
      if (!ethereum) {
        alert("Ethereum provider not found.");
        return;
      }

      // Request account access from Rainbow Wallet
      const accounts = await ethereum.request({
        method: "eth_requestAccounts",
      });

      // Store all available addresses
      if (accounts && accounts.length > 0) {
        const validAddresses = accounts.filter((addr: any): addr is string => 
          addr && typeof addr === "string" && addr.trim().length > 0
        );
        if (validAddresses.length > 0) {
          setAvailableAddresses(validAddresses);
          // If only one address, auto-select it
          if (validAddresses.length === 1) {
            setOwnerAddress(validAddresses[0]);
          }
        }
      }

      // Also connect via wagmi for compatibility
      if (injectedConnector) {
        await connect({ connector: injectedConnector });
        // Wait for wagmi to update
        await new Promise((resolve) => setTimeout(resolve, 300));
      }
    } catch (error: any) {
      if (error.code === 4001) {
        alert("User rejected the connection request.");
      } else {
        console.error("Error connecting to Rainbow Wallet:", error);
        alert(
          `Failed to connect to Rainbow Wallet: ${error.message || "Unknown error"}`,
        );
      }
    }
  };

  const handleConnectCoinbaseWallet = async () => {
    // Handle Solana connection
    if (connectionChainType === "solana") {
      try {
        if (!isCoinbaseWalletSolanaAvailable()) {
          alert(
            "Coinbase Wallet Solana wallet is not available. Please ensure Coinbase Wallet is installed and unlocked.",
          );
          return;
        }

        console.log("[Coinbase Wallet Debug] Connecting to Solana...");
        const solanaAddress = await connectCoinbaseWalletSolana();
        console.log("[Coinbase Wallet Debug] Solana address received:", solanaAddress);

        if (solanaAddress && typeof solanaAddress === "string") {
          setAvailableAddresses([solanaAddress]);
          setOwnerAddress(solanaAddress);
          console.log("[Coinbase Wallet Debug] Solana connection successful");
        }
      } catch (error: any) {
        console.error("[Coinbase Wallet Debug] Solana connection error:", error);
        if (error.code === 4001 || error.message?.includes("rejected")) {
          alert("User rejected the Solana connection request.");
        } else {
          alert(
            `Failed to connect to Coinbase Wallet Solana: ${error.message || "Unknown error"}`,
          );
        }
      }
      return;
    }

    // Handle Ethereum connection
    // Check availability
    if (!isCoinbaseWalletEthereumDetected) {
      console.log("[Coinbase Wallet Debug] Not immediately available, checking with polling...");
      const detected: boolean = await waitForCoinbaseWallet(3000, 100);
      if (detected) {
        console.log("[Coinbase Wallet Debug] Coinbase Wallet detected via polling!");
      } else {
        alert(
          "Coinbase Wallet is not installed. Please install Coinbase Wallet extension from the official Chrome Web Store (extension ID: hnfanknocfeofbddgcijnmhnfnkdnaad).",
        );
        return;
      }
    }

    try {
      // Use the helper function to get Coinbase Wallet provider
      let coinbaseProvider = getCoinbaseWalletProvider();
      
      console.log("[Coinbase Wallet Debug] Initial provider check:", coinbaseProvider ? "Found" : "Not found");
      console.log("[Coinbase Wallet Debug] window.ethereum.isCoinbaseWallet:", (window as any).ethereum?.isCoinbaseWallet);

      // If not immediately available, wait for it with polling
      if (!coinbaseProvider) {
        console.log("[Coinbase Wallet Debug] Provider not immediately available, waiting...");
        const detected: boolean = await waitForCoinbaseWallet(3000, 100);
        if (detected) {
          coinbaseProvider = getCoinbaseWalletProvider();
          console.log("[Coinbase Wallet Debug] Provider found after polling:", coinbaseProvider ? "Yes" : "No");
        }
      }

      if (!coinbaseProvider) {
        alert(
          "Coinbase Wallet provider not found. Please ensure Coinbase Wallet is installed and enabled. Verify you're using the official extension from the Chrome Web Store.",
        );
        return;
      }

      // Request account access from Coinbase Wallet directly
      // This will trigger the Coinbase Wallet popup
      const accounts = await coinbaseProvider.request({
        method: "eth_requestAccounts",
      });

      // Store all available addresses
      if (accounts && accounts.length > 0) {
        const validAddresses = accounts.filter((addr: any): addr is string => 
          addr && typeof addr === "string" && addr.trim().length > 0
        );
        if (validAddresses.length > 0) {
          setAvailableAddresses(validAddresses);
          // If only one address, auto-select it
          if (validAddresses.length === 1) {
            setOwnerAddress(validAddresses[0]);
          }
        }
      }

      // Also connect via wagmi for compatibility
      const coinbaseConnector = connectors.find(
        (c) => c.id === "injected" || c.name?.toLowerCase().includes("coinbase")
      );
      if (coinbaseConnector) {
        await connect({ connector: coinbaseConnector });
        await new Promise((resolve) => setTimeout(resolve, 300));
      } else {
        console.warn(
          "Coinbase Wallet connector not found. Please ensure Coinbase Wallet is installed.",
        );
      }
    } catch (error: any) {
      if (error.code === 4001) {
        alert("User rejected the connection request.");
      } else {
        console.error("Error connecting to Coinbase Wallet:", error);
        alert(
          `Failed to connect to Coinbase Wallet: ${error.message || "Unknown error"}`,
        );
      }
    }
  };

  const handleConnectOKX = async () => {
    // Check if OKX wallet is available at all
    if (!isOKXWalletSDKAvailable()) {
      alert(
        "OKX wallet is not installed. Please install OKX wallet extension.",
      );
      return;
    }

    try {
      let address: string;
      let addresses: string[] = [];

      // Use OKX SDK based on the selected chain type
      if (connectionChainType === "ethereum") {
        // Smart detection: If OKX Solana AND Bitcoin are both detected, 
        // then Ethereum provider is very likely OKX (even if not immediately available)
        const hasOKXSolana = isOKXSolanaSDKAvailable();
        const hasOKXBitcoin = isOKXBitcoinSDKAvailable();
        const hasOKXMultiChain = hasOKXSolana && hasOKXBitcoin;
        
        // Check if OKX Ethereum provider is available
        const hasOKXEthereum = isOKXEthereumSDKAvailable();
        
        // If multi-chain detected but Ethereum provider not available, 
        // try using window.ethereum directly (OKX might have injected it)
        if (!hasOKXEthereum) {
          if (hasOKXMultiChain && (window as any).ethereum) {
            // OKX is detected via multi-chain, and window.ethereum exists
            // This is very likely OKX's Ethereum provider
            console.log("[OKX Debug] Multi-chain OKX detected, using window.ethereum as OKX provider");
            // Continue with connection using window.ethereum
          } else {
            alert(
              "OKX Ethereum provider is not available. Please ensure:\n" +
              "1. OKX wallet extension is installed and unlocked\n" +
              "2. OKX wallet is switched to Ethereum network\n" +
              "3. You have an Ethereum account in OKX\n\n" +
              "If OKX is detected for Solana/Bitcoin but Ethereum provider is missing, " +
              "please switch OKX wallet to Ethereum network in the extension."
            );
            return;
          }
        }

        console.log("[OKX Debug] Connecting to OKX Ethereum using OKX SDK...");
        console.log("[OKX Debug] Multi-chain detection:", { hasOKXSolana, hasOKXBitcoin, hasOKXMultiChain });
        console.log("[OKX Debug] Ethereum provider check:", { 
          hasOKXEthereum, 
          hasOkxwalletEthereum: !!(window as any).okxwallet?.ethereum,
          hasEthereumIsOKX: !!(window as any).ethereum?.isOKX,
          hasWindowEthereum: !!(window as any).ethereum
        });
        
        // Use OKX SDK to get Ethereum address
        // If multi-chain detected but okxwallet.ethereum not available, 
        // getOKXEthereumAddress will try window.ethereum as fallback
        try {
          address = await getOKXEthereumAddress();
          addresses = [address];
        } catch (error: any) {
          // If OKX SDK method fails but we have multi-chain detection and window.ethereum,
          // try using window.ethereum directly
          if (hasOKXMultiChain && (window as any).ethereum) {
            console.log("[OKX Debug] OKX SDK method failed, trying window.ethereum directly...");
            const accounts = await (window as any).ethereum.request({ method: 'eth_requestAccounts' });
            if (accounts && accounts.length > 0) {
              address = accounts[0];
              addresses = [accounts];
            } else {
              throw new Error("No accounts returned from OKX Ethereum wallet");
            }
          } else {
            throw error;
          }
        }

        // Also connect via wagmi for compatibility
        if (injectedConnector) {
          await connect({ connector: injectedConnector });
          // Wait for wagmi to update
          await new Promise((resolve) => setTimeout(resolve, 300));
        }
      } else if (connectionChainType === "solana") {
        // Check if OKX Solana provider is available
        if (!isOKXSolanaSDKAvailable()) {
          alert(
            "OKX Solana provider is not available. Please ensure OKX wallet is installed and switched to Solana network.",
          );
          return;
        }

        console.log("[OKX Debug] Connecting to OKX Solana using OKX SDK...");
        // Use OKX SDK to get Solana address
        address = await getOKXSolanaAddress();
        addresses = [address];
      } else if (connectionChainType === "bitcoin") {
        // Check if OKX Bitcoin provider is available
        if (!isOKXBitcoinSDKAvailable()) {
          alert(
            "OKX Bitcoin provider is not available. Please ensure:\n" +
            "1. OKX wallet extension is unlocked\n" +
            "2. OKX wallet is switched to Bitcoin network (not Ethereum)\n" +
            "3. You have a Bitcoin account in OKX\n\n" +
            "After switching to Bitcoin network in OKX, refresh this page and try again.",
          );
          return;
        }

        console.log("[OKX Debug] Connecting to OKX Bitcoin using OKX SDK...");
        // Use OKX SDK to get Bitcoin address
        address = await getOKXBitcoinAddress();
        addresses = [address];
      } else {
        alert(`Unsupported chain type: ${connectionChainType}`);
        return;
      }

      // Store all available addresses (filter out invalid values)
      const validAddresses = addresses.filter((addr: any): boolean => 
        Boolean(addr && typeof addr === "string" && addr.trim().length > 0)
      ) as string[];
      if (validAddresses.length > 0) {
        setAvailableAddresses(validAddresses);
        // Set the owner address
        if (address && typeof address === "string") {
          setOwnerAddress(address);
        }
        console.log(`[OKX Debug] Successfully connected to OKX ${connectionChainType}:`, address);
      } else {
        throw new Error("No addresses returned from OKX wallet");
      }
    } catch (error: any) {
      if (error.code === 4001 || error.message?.includes("rejected") || error.message?.includes("User rejected")) {
        alert("User rejected the OKX connection request.");
      } else {
        console.error(`[OKX Debug] Error connecting to OKX ${connectionChainType}:`, error);
        const errorMessage = error.message || "Unknown error";
        alert(
          `Failed to connect to OKX ${connectionChainType}: ${errorMessage}\n\n` +
          "Please ensure:\n" +
          "1. OKX wallet extension is installed and unlocked\n" +
          `2. OKX wallet is switched to ${connectionChainType} network\n` +
          `3. You have a ${connectionChainType} account in OKX\n` +
          "4. Try refreshing the page and connecting again"
        );
      }
    }
  };

  // Check if ANY wallet is available for ANY chain
  const hasAnyWalletAvailable = (): boolean => {
    // Check Ethereum wallets
    const hasEthereumWallet = 
      isOneKeyAvailable ||
      isOKXEthereumAvailable ||
      isBinanceAvailable ||
      isMetaMaskAvailable ||
      isPhantomAvailable ||
      (typeof window !== "undefined" && (window as any).ethereum);
    
    // Check Solana wallets
    const hasSolanaWallet = 
      isPhantomSolanaAvailable() ||
      isOneKeySolanaSDKAvailable() ||
      isOKXSolanaSDKAvailable() ||
      isBinanceSolanaAvailable();
    
    // Check Bitcoin wallets
    const hasBitcoinWallet = 
      isBitcoinWalletAvailable() ||
      isOKXWalletInstalled() ||
      isOneKeyBitcoinAvailable;
    
    return hasEthereumWallet || hasSolanaWallet || hasBitcoinWallet;
  };

  // Handler map for wallet names to their connection handlers
  const walletHandlerMap: Partial<Record<string, () => Promise<void>>> = {
    "OneKey": handleConnectOneKey,
    "OKX": handleConnectOKX,
    "Bitget": handleConnectBitget,
    "TokenPocket": handleConnectTokenPocket,
    "Coinbase Wallet": handleConnectCoinbaseWallet,
    "Rainbow": handleConnectRainbow,
    "Binance": handleConnectBinance,
    "MetaMask": handleConnectMetaMask,
    "Phantom": handleConnectPhantom,
    "Bitcoin Wallet": handleConnectBitcoin,
  };

  // Determine the best wallet to connect to based on chain type and availability
  // Uses the shared utility from extdetection.ts for wallet detection logic
  const getBestWallet = (): { wallet: string; handler: () => Promise<void> } | null => {
    // Use shared utility to get the best wallet name
    const bestWalletName = getBestWalletName(connectionChainType);
    
    if (!bestWalletName) {
      return null;
    }

    // Special handling for Bitcoin Wallet - use the preferred wallet's actual name
    if (bestWalletName === "Bitcoin Wallet" && connectionChainType === "bitcoin") {
      const preferredWallet = getPreferredBitcoinWallet();
      const walletName = preferredWallet?.name || "Bitcoin Wallet";
      const handler = walletHandlerMap["Bitcoin Wallet"];
      if (handler) {
        return { wallet: walletName, handler };
      }
    }

    // Map wallet name to handler
    const handler = walletHandlerMap[bestWalletName];
    if (handler) {
      return { wallet: bestWalletName, handler };
    }

    return null;
  };

  // Smart connect handler that uses the best available wallet
  const handleSmartConnect = async () => {
    const bestWallet = getBestWallet();
    if (!bestWallet) {
      // No wallet available - show appropriate error message
      if (connectionChainType === "ethereum") {
        alert(
          "No Ethereum wallet detected. Please install MetaMask, Phantom, OneKey, OKX, Bitget, or Binance wallet."
        );
      } else if (connectionChainType === "solana") {
        alert(
          "No Solana wallet detected. Please install Phantom, OneKey, OKX, Bitget, or Binance wallet extension for Solana support."
        );
      } else if (connectionChainType === "bitcoin") {
        if (isOKXWalletInstalled()) {
          alert(
            "OKX wallet detected, but Bitcoin provider is not available. Please switch OKX wallet to Bitcoin network in the extension, then refresh this page."
          );
        } else {
          alert(
            "No Bitcoin wallet detected. Please install OneKey, OKX, Bitget, Unisat, or Xverse wallet extension."
          );
        }
      }
      return;
    }

    // Connect using the best available wallet
    await bestWallet.handler();
  };

  return (
    <div className="component">
      <h3>Agent Delegation</h3>
      <p>Approve an agent private key to act on behalf of the owner wallet</p>

      {/* Step 1: Connect Wallet */}
      <div
        className={`agent-delegation-step ${hasWalletConnection ? "connected" : ""}`}
      >
        <h4>
          Step 1: Connect Wallet {hasWalletExtensionConnection && (effectiveEthereumAddress || ((isSolanaConnected || isBitcoinConnected) && ownerAddress)) && "✓"}
        </h4>

        {!hasWalletConnection ? (
          <div>
            {/* Check if any wallet is available for any chain */}
            {hasAnyWalletAvailable() ? (
              <>
                {/* Chain Type Selector - only show if wallets are available */}
                <div className="form-group" style={{ marginBottom: "1.5rem" }}>
                  <label htmlFor="connectionChainType" style={{ marginBottom: "0.5rem", display: "block" }}>
                    <strong>Select Chain Type:</strong>
                  </label>
                  <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                    <button
                      className={`button ${connectionChainType === "ethereum" ? "" : "secondary"}`}
                      onClick={() => setConnectionChainType("ethereum")}
                      style={{ flex: 1, minWidth: "100px" }}
                    >
                      Ethereum
                    </button>
                    {(isPhantomSolanaAvailable() || isOneKeySolanaAvailable || isOKXSolanaSDKAvailable() || isBinanceSolanaAvailable()) && (
                      <button
                        className={`button ${connectionChainType === "solana" ? "" : "secondary"}`}
                        onClick={() => setConnectionChainType("solana")}
                        style={{ flex: 1, minWidth: "100px" }}
                      >
                        Solana
                      </button>
                    )}
                    {/* Show Bitcoin option if Bitcoin wallets are available OR OKX wallet is installed */}
                    {(isBitcoinWalletAvailable() || isOKXWalletInstalled()) && (
                      <button
                        className={`button ${connectionChainType === "bitcoin" ? "" : "secondary"}`}
                        onClick={() => setConnectionChainType("bitcoin")}
                        style={{ flex: 1, minWidth: "100px" }}
                      >
                        Bitcoin
                      </button>
                    )}
                  </div>
                  <small className="agent-delegation-small-text" style={{ display: "block", marginTop: "0.5rem" }}>
                    {connectionChainType === "ethereum"
                      ? "Connect to Ethereum network (MetaMask, Phantom Ethereum, OneKey, OKX, Binance)"
                      : connectionChainType === "solana"
                        ? "Connect to Solana network (Phantom Solana, OneKey Solana, OKX Solana, Binance Solana)"
                        : "Connect to Bitcoin network (OneKey, Unisat, Xverse, OKX). If using OKX, ensure it's switched to Bitcoin network."}
                  </small>
                </div>

                <div className="agent-delegation-wallet-buttons">
                  {/* Single smart connect button */}
                  {(() => {
                    const bestWallet = getBestWallet();
                    if (bestWallet) {
                      return (
                        <button
                          className="button"
                          onClick={handleSmartConnect}
                          disabled={isConnecting || (connectionChainType === "ethereum" && !injectedConnector)}
                        >
                          {isConnecting
                            ? "Connecting..."
                            : `Connect ${bestWallet.wallet}${connectionChainType !== "ethereum" ? ` (${connectionChainType.charAt(0).toUpperCase() + connectionChainType.slice(1)})` : ""}`}
                        </button>
                      );
                    } else {
                      // No wallet available for selected chain type - show error message
                      if (connectionChainType === "ethereum") {
                        return (
                          <p className="agent-delegation-error-message">
                            No Ethereum wallet detected. Please install MetaMask, Phantom, OneKey, OKX, Rainbow, or Binance wallet.
                          </p>
                        );
                      } else if (connectionChainType === "solana") {
                        return (
                          <p className="agent-delegation-error-message">
                            No Solana wallet detected. Please install Phantom, OneKey, OKX, Bitget, or Binance wallet extension for Solana support.
                          </p>
                        );
                      } else if (connectionChainType === "bitcoin") {
                        return (
                          <p className="agent-delegation-error-message">
                            {isOKXWalletInstalled() || (window as any).bitkeep?.isBitKeep
                              ? "Wallet detected, but Bitcoin provider is not available. Please switch your wallet to Bitcoin network in the extension, then refresh this page."
                              : "No Bitcoin wallet detected. Please install OneKey, OKX, Bitget, Unisat, or Xverse wallet extension."}
                          </p>
                        );
                      }
                      return null;
                    }
                  })()}
                </div>
              </>
            ) : (
              /* No wallets detected at all - show error message only */
              <div className="agent-delegation-wallet-buttons">
                <p className="agent-delegation-error-message" style={{ marginBottom: "1rem" }}>
                  <strong>No wallet detected</strong>
                </p>
                <p className="agent-delegation-error-message">
                  Please install a compatible wallet extension to continue:
                </p>
                <ul style={{ textAlign: "left", marginTop: "0.5rem", marginBottom: "1rem" }}>
                  <li><strong>Ethereum:</strong> MetaMask, Phantom, OneKey, OKX, Rainbow, Bitget, or Binance wallet</li>
                  <li><strong>Solana:</strong> Phantom, OneKey, OKX, Bitget, or Binance wallet</li>
                  <li><strong>Bitcoin:</strong> OneKey, OKX, Bitget, Unisat, or Xverse wallet</li>
                </ul>
                <p className="agent-delegation-small-text" style={{ marginTop: "0.5rem" }}>
                  After installing a wallet, refresh this page to connect.
                </p>
              </div>
            )}
          </div>
        ) : availableAddresses.length > 0 && !hasWalletExtensionConnection ? (
          <div>
            <p style={{ marginBottom: "1rem" }}>
              <strong>Connected:</strong> {connectedWallet || "Wallet"}
            </p>
            <div className="form-group">
              <label htmlFor="addressSelect">Select an address to use:</label>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.5rem",
                }}
              >
                {availableAddresses
                  .filter((address) => address && typeof address === "string")
                  .map((address) => (
                    <button
                      key={address}
                      className={`button ${ownerAddress?.toLowerCase() === address?.toLowerCase() ? "" : "secondary"}`}
                      onClick={() => setOwnerAddress(address)}
                      style={{
                        textAlign: "left",
                        justifyContent: "flex-start",
                        fontFamily: "monospace",
                        fontSize: "0.9rem",
                      }}
                    >
                      {address}
                    </button>
                  ))}
              </div>
            </div>
            <button
              className="button secondary"
              onClick={async () => {
                // Disconnect based on chain type
                if (connectionChainType === "solana") {
                  // Try OneKey Solana first
                  const oneKeySolanaProvider = getOneKeySolanaProvider();
                  if (oneKeySolanaProvider && oneKeySolanaProvider.isConnected) {
                    try {
                      await oneKeySolanaProvider.disconnect();
                    } catch (error) {
                      console.error("Error disconnecting OneKey Solana:", error);
                    }
                  }
                  // Try Phantom Solana
                  const solanaProvider = getPhantomSolanaProvider();
                  if (solanaProvider && solanaProvider.isConnected) {
                    try {
                      await solanaProvider.disconnect();
                    } catch (error) {
                      console.error("Error disconnecting Phantom Solana:", error);
                    }
                  }
                  // Try OKX Solana
                  const okxSolanaProvider = getOKXSolanaProvider();
                  if (okxSolanaProvider && okxSolanaProvider.isConnected) {
                    try {
                      await okxSolanaProvider.disconnect();
                    } catch (error) {
                      console.error("Error disconnecting OKX Solana:", error);
                    }
                  }
                  // Try Binance Solana
                  const binanceSolanaProvider = getBinanceSolanaProvider();
                  if (binanceSolanaProvider && binanceSolanaProvider.isConnected) {
                    try {
                      await binanceSolanaProvider.disconnect();
                    } catch (error) {
                      console.error("Error disconnecting Binance Solana:", error);
                    }
                  }
                } else if (connectionChainType === "ethereum") {
                  disconnect();
                }
                // Clear state
                setAvailableAddresses([]);
                setOwnerAddress("");
              }}
              style={{ marginTop: "1rem" }}
            >
              Disconnect
            </button>
          </div>
        ) : (
          <div className="agent-delegation-connected-info">
            <div>
              <strong>Connected:</strong> {connectedWallet || (isBitcoinConnected ? getPreferredBitcoinWallet()?.name || "Bitcoin Wallet" : null)}
              {isSolanaConnected && (
                <span style={{ marginLeft: "0.5rem", fontSize: "0.9rem", opacity: 0.8 }}>
                  (Solana)
                </span>
              )}
              {isBitcoinConnected && (
                <span style={{ marginLeft: "0.5rem", fontSize: "0.9rem", opacity: 0.8 }}>
                  (Bitcoin)
                </span>
              )}
              {(effectiveEthereumAddress || ((isSolanaConnected || isBitcoinConnected) && ownerAddress)) && (
                <div className="agent-delegation-address">
                  Address: {effectiveEthereumAddress || ownerAddress}
                </div>
              )}
            </div>
            <button
              className="button secondary"
              onClick={async () => {
                // Disconnect based on chain type
                if (connectionChainType === "solana" || isSolanaConnected) {
                  // Try Phantom Solana first
                  const solanaProvider = getPhantomSolanaProvider();
                  if (solanaProvider && solanaProvider.isConnected) {
                    try {
                      await solanaProvider.disconnect();
                    } catch (error) {
                      console.error("Error disconnecting Phantom Solana:", error);
                    }
                  }
                  // Try OKX Solana
                  const okxSolanaProvider = getOKXSolanaProvider();
                  if (okxSolanaProvider && okxSolanaProvider.isConnected) {
                    try {
                      await okxSolanaProvider.disconnect();
                    } catch (error) {
                      console.error("Error disconnecting OKX Solana:", error);
                    }
                  }
                  // Try Binance Solana
                  const binanceSolanaProvider = getBinanceSolanaProvider();
                  if (binanceSolanaProvider && binanceSolanaProvider.isConnected) {
                    try {
                      await binanceSolanaProvider.disconnect();
                    } catch (error) {
                      console.error("Error disconnecting Binance Solana:", error);
                    }
                  }
                } else if (connectionChainType === "bitcoin" || isBitcoinConnected) {
                  // Bitcoin wallets don't have a standard disconnect method
                  // Just clear the state
                  console.log("[Bitcoin Debug] Disconnecting Bitcoin wallet");
                } else {
                  // Disconnect Ethereum via wagmi
                  disconnect();
                }
                // Clear all state
                setAvailableAddresses([]);
                setOwnerAddress("");
              }}
            >
              Disconnect
            </button>
          </div>
        )}
      </div>

      {/* Step 2: Configure Agent Delegation (only show if wallet extension is connected and address is selected) */}
      {hasWalletExtensionConnection && (effectiveEthereumAddress || ((isSolanaConnected || isBitcoinConnected) && ownerAddress)) ? (
        <div
          className={`agent-delegation-step ${isWalletReady ? "connected" : ""}`}
        >
          <h4>Step 2: Configure Agent Delegation {isWalletReady && "✓"}</h4>
          {/* Show credential restoration indicator */}
          {credentialsRestored && (
            <div
              style={{
                padding: "0.75rem",
                marginBottom: "1rem",
                background: "rgba(76, 175, 80, 0.1)",
                border: "1px solid rgba(76, 175, 80, 0.3)",
                borderRadius: "8px",
                color: "rgba(76, 175, 80, 0.9)",
                fontSize: "0.9rem",
              }}
            >
              ✓ Agent credentials restored from previous session
            </div>
          )}

          <div className="form-group">
            <label htmlFor="ownerAddress">Owner Address:</label>
            <input
              id="ownerAddress"
              type="text"
              value={effectiveEthereumAddress || ownerAddress}
              readOnly
              style={{
                fontFamily: "monospace",
                fontSize: "0.9rem",
                backgroundColor: "rgba(255, 255, 255, 0.05)",
                cursor: "not-allowed",
              }}
            />
            <small className="agent-delegation-small-text">
              This address is from your connected {connectedWallet || "wallet"} extension
              {isSolanaConnected ? " (Solana)" : isBitcoinConnected ? " (Bitcoin)" : effectiveEthereumAddress ? " (Ethereum)" : ""}.
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="agentAddress">Agent Address:</label>
            <div className="agent-delegation-input-group">
              <input
                id="agentAddress"
                type="text"
                value={agentAddress}
                onChange={(e) => setAgentAddress(e.target.value)}
                placeholder="0x..."
                className="agent-delegation-input-flex"
              />
              <button
                className="button secondary agent-delegation-button-compact"
                onClick={generateAgentKeypair}
                disabled={loading || !isReady}
              >
                Generate Keypair
              </button>
            </div>
          </div>

          {agentPrivateKey && (
            <div className="form-group">
              <label htmlFor="agentPrivateKey">
                Agent Private Key (save securely):
              </label>
              <input
                id="agentPrivateKey"
                type="text"
                value={agentPrivateKey}
                readOnly
                className="agent-delegation-private-key"
              />
              <div style={{ marginTop: "0.5rem" }}>
                <button
                  className="button secondary"
                  onClick={() => {
                    clearAgentCredentials();
                    setCredentialsRestored(false);
                  }}
                  style={{ fontSize: "0.85rem", padding: "0.5rem 1rem" }}
                >
                  Clear Agent Credentials
                </button>
                <small
                  className="agent-delegation-small-text"
                  style={{ display: "block", marginTop: "0.5rem" }}
                >
                  Credentials are stored in browser session storage and will be cleared when browser closes.
                </small>
              </div>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="agentName">Agent Name (optional):</label>
            <input
              id="agentName"
              type="text"
              value={agentName}
              onChange={(e) => setAgentName(e.target.value)}
              placeholder="enter you agent name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="expiresInDays">Expires In Days:</label>
            <input
              id="expiresInDays"
              type="number"
              value={expiresInDays}
              onChange={(e) =>
                setExpiresInDays(parseInt(e.target.value) || 180)
              }
              min="1"
              max="365"
            />
          </div>

          <div className="form-group">
            <label htmlFor="permissions">Permissions (comma-separated):</label>
            <input
              id="permissions"
              type="text"
              value={permissions}
              onChange={(e) => setPermissions(e.target.value)}
              placeholder="clob::submit_order,clob::cancel_order"
            />
            <small className="agent-delegation-small-text">
              Example: clob::submit_order, clob::cancel_order,
              clob::modify_order
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="nonce">Nonce:</label>
            <input
              id="nonce"
              type="text"
              value={nonce}
              onChange={(e) => setNonce(e.target.value)}
              placeholder={Date.now().toString()}
            />
          </div>
        </div>
      ) : (
        <div className="agent-delegation-step disabled">
          <h4>Step 2: Configure Agent Delegation</h4>
          <p className="agent-delegation-disabled-text">
            {hasWalletConnection && !hasWalletExtensionConnection
              ? "Please select an address in Step 1 to continue."
              : "Please connect a wallet extension in Step 1 to continue."}
          </p>
        </div>
      )}

      {/* Step 3: Prepare and Sign (only show if wallet is ready) */}
      {isWalletReady ? (
        <div className="agent-delegation-step">
          <h4>Step 3: Prepare and Sign Agent Approval</h4>

          <div className="agent-delegation-button-group">
            {isMetaMaskConnected && (
              <button
                className="button"
                onClick={handleSignAgentApproval}
                disabled={isSigning || !wagmiAddress || !agentAddress}
              >
                {isSigning
                  ? "Signing with MetaMask..."
                  : "Sign Agent Approval (MetaMask)"}
              </button>
            )}

            {(isPhantomConnected || (connectionChainType === "solana" && isSolanaConnected && !getOneKeySolanaProvider()?.isConnected && !getOKXSolanaProvider()?.isConnected && !getBitgetSolanaProvider()?.isConnected && !getBinanceSolanaProvider()?.isConnected)) && (
              <button
                className="button"
                onClick={handleSignAgentApproval}
                disabled={isSigning || (connectionChainType === "ethereum" && !wagmiAddress) || !agentAddress || !ownerAddress}
              >
                {isSigning
                  ? "Signing with Phantom..."
                  : connectionChainType === "solana"
                    ? "Sign Agent Approval (Phantom Solana)"
                    : "Sign Agent Approval (Phantom)"}
              </button>
            )}

            {(isOneKeyConnected || 
              (connectionChainType === "solana" && isSolanaConnected && getOneKeySolanaProvider()?.isConnected) ||
              (connectionChainType === "bitcoin" && isBitcoinConnected && isOneKeyBitcoinAvailable)) && (
              <button
                className="button"
                onClick={handleSignAgentApproval}
                disabled={isSigning || (connectionChainType === "ethereum" && !wagmiAddress) || !agentAddress || !ownerAddress}
              >
                {isSigning
                  ? "Signing with OneKey..."
                  : connectionChainType === "solana"
                    ? "Sign Agent Approval (OneKey Solana)"
                    : connectionChainType === "bitcoin"
                      ? "Sign Agent Approval (OneKey Bitcoin)"
                      : "Sign Agent Approval (OneKey)"}
              </button>
            )}

            {(connectionChainType === "solana" && isSolanaConnected && getBitgetSolanaProvider()?.isConnected) && (
              <button
                className="button"
                onClick={handleSignAgentApproval}
                disabled={isSigning || !agentAddress || !ownerAddress}
              >
                {isSigning
                  ? "Signing with Bitget..."
                  : "Sign Agent Approval (Bitget Solana)"}
              </button>
            )}

            {(connectionChainType === "solana" && isSolanaConnected && getBinanceSolanaProvider()?.isConnected) && (
              <button
                className="button"
                onClick={handleSignAgentApproval}
                disabled={isSigning || !agentAddress || !ownerAddress}
              >
                {isSigning
                  ? "Signing with Binance..."
                  : "Sign Agent Approval (Binance Solana)"}
              </button>
            )}

            {(isOKXConnected ||
              (connectionChainType === "solana" && isSolanaConnected && getOKXSolanaProvider()?.isConnected && !getBitgetSolanaProvider()?.isConnected && !getBinanceSolanaProvider()?.isConnected) ||
              (connectionChainType === "bitcoin" && isBitcoinConnected && isOKXBitcoinSDKAvailable())) && (
              <button
                className="button"
                onClick={handleSignAgentApproval}
                disabled={isSigning || (!wagmiAddress && connectionChainType === "ethereum") || !agentAddress || !ownerAddress}
              >
                {isSigning
                  ? "Signing with OKX..."
                  : `Sign Agent Approval (OKX${connectionChainType === "ethereum" ? "" : ` ${connectionChainType}`})`}
              </button>
            )}

            {isBinanceConnected && (
              <button
                className="button"
                onClick={handleSignAgentApproval}
                disabled={isSigning || !wagmiAddress || !agentAddress}
              >
                {isSigning
                  ? "Signing with Binance..."
                  : "Sign Agent Approval (Binance)"}
              </button>
            )}

            {isBitgetConnected && (
              <button
                className="button"
                onClick={handleSignAgentApproval}
                disabled={isSigning || !wagmiAddress || !agentAddress}
              >
                {isSigning
                  ? "Signing with Bitget..."
                  : "Sign Agent Approval (Bitget)"}
              </button>
            )}

            {isRainbowConnected && (
              <button
                className="button"
                onClick={handleSignAgentApproval}
                disabled={isSigning || !wagmiAddress || !agentAddress}
              >
                {isSigning
                  ? "Signing with Rainbow..."
                  : "Sign Agent Approval (Rainbow)"}
              </button>
            )}

            {/* TokenPocket support */}
            {((connectionChainType === "ethereum" && isTokenPocketEthereumDetected && wagmiIsConnected) ||
              (connectionChainType === "solana" && isSolanaConnected && isTokenPocketSolanaDetected && getTokenPocketSolanaProvider()?.isConnected)) && (
              <button
                className="button"
                onClick={handleSignAgentApproval}
                disabled={isSigning || (connectionChainType === "ethereum" && !wagmiAddress) || !agentAddress || !ownerAddress}
              >
                {isSigning
                  ? "Signing with TokenPocket..."
                  : `Sign Agent Approval (TokenPocket${connectionChainType === "ethereum" ? "" : ` ${connectionChainType}`})`}
              </button>
            )}

            {/* Coinbase Wallet support */}
            {((connectionChainType === "ethereum" && isCoinbaseWalletEthereumDetected && wagmiIsConnected) ||
              (connectionChainType === "solana" && isSolanaConnected && isCoinbaseWalletSolanaDetected && getCoinbaseWalletSolanaProvider()?.isConnected)) && (
              <button
                className="button"
                onClick={handleSignAgentApproval}
                disabled={isSigning || (connectionChainType === "ethereum" && !wagmiAddress) || !agentAddress || !ownerAddress}
              >
                {isSigning
                  ? "Signing with Coinbase Wallet..."
                  : `Sign Agent Approval (Coinbase Wallet${connectionChainType === "ethereum" ? "" : ` ${connectionChainType}`})`}
              </button>
            )}

            {/* Fallback button: Show when wallet is ready but no specific wallet button matches */}
            {!isMetaMaskConnected &&
              !(isPhantomConnected || (connectionChainType === "solana" && isSolanaConnected && !getOneKeySolanaProvider()?.isConnected && !getOKXSolanaProvider()?.isConnected && !getBitgetSolanaProvider()?.isConnected && !getBinanceSolanaProvider()?.isConnected)) &&
              !(isOneKeyConnected || 
                (connectionChainType === "solana" && isSolanaConnected && getOneKeySolanaProvider()?.isConnected) ||
                (connectionChainType === "bitcoin" && isBitcoinConnected && isOneKeyBitcoinAvailable)) &&
              !(connectionChainType === "solana" && isSolanaConnected && getBitgetSolanaProvider()?.isConnected) &&
              !(connectionChainType === "solana" && isSolanaConnected && getBinanceSolanaProvider()?.isConnected) &&
              !(isOKXConnected ||
                (connectionChainType === "solana" && isSolanaConnected && getOKXSolanaProvider()?.isConnected && !getBitgetSolanaProvider()?.isConnected && !getBinanceSolanaProvider()?.isConnected) ||
                (connectionChainType === "bitcoin" && isBitcoinConnected && isOKXBitcoinSDKAvailable())) &&
              !isBinanceConnected &&
              !isBitgetConnected &&
              !isRainbowConnected &&
              !((connectionChainType === "ethereum" && isTokenPocketEthereumDetected && wagmiIsConnected) ||
                (connectionChainType === "solana" && isSolanaConnected && isTokenPocketSolanaDetected && getTokenPocketSolanaProvider()?.isConnected)) &&
              !((connectionChainType === "ethereum" && isCoinbaseWalletEthereumDetected && wagmiIsConnected) ||
                (connectionChainType === "solana" && isSolanaConnected && isCoinbaseWalletSolanaDetected && getCoinbaseWalletSolanaProvider()?.isConnected)) && (
              <button
                className="button"
                onClick={handleSignAgentApproval}
                disabled={
                  isSigning ||
                  !agentAddress ||
                  !ownerAddress ||
                  (connectionChainType === "ethereum" && !wagmiAddress && !effectiveEthereumAddress)
                }
              >
                {isSigning
                  ? "Signing..."
                  : `Sign Agent Approval${connectedWallet ? ` (${connectedWallet})` : ""}`}
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="agent-delegation-step disabled">
          <h4>Step 3: Prepare and Sign Agent Approval</h4>
          <p className="agent-delegation-disabled-text">
            Please complete Step 1 (connect wallet) and Step 2 (configure
            addresses) to continue.
          </p>
        </div>
      )}

      {(result || signingResult) && (
        <div
          className={`output ${(result || signingResult)?.success ? "success" : "error"}`}
        >
          {(result || signingResult)?.success ? (
            <>
              {(result || signingResult)?.signature && (
                <>
                  <strong>Agent Approval Signature:</strong>
                  <br />
                  {(result || signingResult)?.signature}
                  <br />
                  <br />
                </>
              )}
              {(result || signingResult)?.typedData && (
                <>
                  <strong>EIP-712 Typed Data:</strong>
                  <br />
                  <pre className="agent-delegation-pre">
                    {JSON.stringify(
                      (result || signingResult)?.typedData,
                      null,
                      2,
                    )}
                  </pre>
                  <br />
                  <strong>Usage:</strong>
                  <br />
                  Signature received from wallet. The typed data structure is
                  shown above.
                </>
              )}
            </>
          ) : (
            <>
              <strong>Error:</strong>
              <br />
              {(result || signingResult)?.error}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default AgentDelegation;
